/**
 * SparkLayer PDP/card clean-up.
 *
 * 1) FXT-229: hide Spark PDP stock / pre-order rows in open shadow DOM.
 *    Theme SKU + delay notice stay. Product Code is --spark-pdp-sku: none.
 * 2) ATC labels: D2C card copy, no ({price}). Iy() merges
 *    sparkOptions.translations (dotted keys) at language load — this file
 *    must parse before the Core Script in content_for_header.
 * 3) ATC tokens: Spark app-block :root clobbers assets/fixatech-spark-tokens.css.
 *    Re-assert per-host after Spark inject — do not paint PDP and card the same.
 *    PDP (scheme-3): hard #ffc600 / rgb(255,198,0) + #141414 text. No bag.
 *    PDP buy-box: qty on its row, yellow ATC width:100% of that column
 *    underneath. Live Spark: `flex flex-col gap-4 sm:flex-row` + ATC
 *    `w-full !w-full sm:!w-auto` — sm+ is a short pill beside qty.
 *    Override sm:flex-row → column and kill sm:!w-auto in the open
 *    shadow (and nested shadows). Do not nowrap that row.
 *    Heart / wishlist / shopping-list twin is display:none on spark-pdp,
 *    spark-product-card, and late carousel/recommended tiles — never a
 *    second “Add to cart”. Do not invent another ATC control.
 *    Highlight/hover vars stay #ffc600 so Spark cannot paint rest #e9b217.
 *    Card (scheme-6 / .button.add-to-cart-button): full-width OUTER button
 *    only — #141414 + white bag (20×20) + visible “Add to cart” (Geist
 *    16/400, radius 12, padding 16×24). Never paint an inner
 *    .bg-btn-raised-bg chip. Spark’s .text-btn / .font-btn stay in the
 *    tree and stay visible (no font-size 0 / clip / transparent).
 *    :hover paints that same outer node #d9d9d9 / #141414.
 * 4) B2B twin (heart / wishlist / clipboard shopping-list): never treat
 *    it as ATC and never restyle it as a second button. Hide it with
 *    display:none on spark-pdp, spark-product-card, and recommended /
 *    related carousel tiles at every breakpoint so ATC can be width:100%
 *    of the action row. Icon-only failed — live PDP node is heart + full
 *    ATC label; card is a square heart beside ATC. Nested shadows and
 *    Horizon siblings of ATC are the same twin. Late Section Rendering
 *    (product-recommendations) is watched on that host only — no
 *    document-wide MutationObserver.
 * 5) B2B PDP price: Will prefixes (Votre prix / Prix régulier). Both
 *    amount lines are Flintstock Round / 400 (FXT-247). Sizes stay
 *    32 / 24; sale #141414; RRP muted #333. FXT-248: RRP line opacity
 *    0.6 (net stays 1). Cards keep Geist + 700 net. sparkOptions +
 *    shadow. D2C / cart untouched.
 * 6) FXT-239 tile price: same prefixes on spark-product-card. Bold net /
 *    Votre prix only (700). RRP / Prix régulier stays #333 / 400. Do not
 *    apply PDP 32/24 sizes on cards. No extra script; same host-gated load.
 * 7) FXT-240: spark-product-card fills leftover card height (gallery +
 *    title stay in light DOM and must not shrink). Open shadow column
 *    `div.flex.flex-col.gap-y-2` grows; qty+ATC
 *    `div.flex.flex-col.gap-x-5.gap-y-2` gets margin-top auto. Never
 *    mark the ATC button (it sits in non-flex .min-w-0.grow). Host is
 *    height:auto — height:100% clipped .card-gallery. f84e397 then
 *    applied height:100%/min-height:0 to :host > * and flex:1 1 100%
 *    + min-height:0 on the black ATC, so the button collapsed while
 *    qty stayed. Lock ATC + qty group at flex:0 0 auto / min-height
 *    52px. Do not grow :host > * as a column. Host is flex:1 1 0% +
 *    overflow:hidden so qty+ATC pin inside leftover — overflow:visible
 *    painted the 53px button below .product-grid__card overflow:hidden.
 *    FXT-244: lead-time trees omit gap-x-5. Price / qty / ATC are column
 *    siblings — pin the first commerce child (margin-top:auto). Do not use
 *    :last-child:has(ATC); :has is descendants and misses the button.
 * 8) FXT-242: B2B card ATC must add to cart, not open the PDP.
 *    Horizon product-card.navigateToProduct uses event.target.closest
 *    ('button') in the light DOM. Live ATC composedPath is div×5 →
 *    #document-fragment → spark-product-card (no <a>, no <button>),
 *    so closest('button') misses and the card navigates. Document
 *    capture: if that path includes spark-product-card and the click
 *    is ATC chrome, stopPropagation and add with
 *    window.spark.updateCart({ products:[{ sku, adjustQuantity }] }).
 *    Qty +/− is not intercepted. Image/title stay in light DOM and
 *    still open the PDP. No hoist, no synthetic click, no nav wrap.
 *    D2C native ATC and PDP yellow ATC stay untouched.
 * 9) FXT-254: B2B Spark ATC hides « au panier » at Horizon mobile
 *    (max-width: 749px — same as header / theme-styles). Wrap the
 *    suffix in .fixatech-spark-atc-label__rest so desktop / D2C keep
 *    the full string. Card stays bag + « Ajouter ». setLabel copy
 *    is unchanged (no new CTA keys).
 */
(() => {
  const CTA = {
    en: {
      'pdp.add-to-order.cta-text': 'Add to cart',
      'product-card.add-to-order.cta-text': 'Add to cart',
    },
    fr: {
      'pdp.add-to-order.cta-text': 'Ajouter au panier',
      'product-card.add-to-order.cta-text': 'Ajouter au panier',
    },
  };
  /* Spark docs: pdp.price.from-prefix + pdp.price.rrp. Blank rrp so Spark
     does not emit Prix conseillé / RRP. JS injects Will’s sale + RRP
     prefixes (from-prefix only applies to “from” prices). */
  const PRICE = {
    en: {
      'pdp.price.from-prefix': 'Your price: ',
      'pdp.price.from': 'Your price: ',
      'pdp.price.rrp': '',
      'product-card.price.from-prefix': 'Your price: ',
      'product-card.price.from': 'Your price: ',
      'product-card.price.rrp': '',
    },
    fr: {
      'pdp.price.from-prefix': 'Votre prix: ',
      'pdp.price.from': 'Votre prix: ',
      'pdp.price.rrp': '',
      'product-card.price.from-prefix': 'Votre prix: ',
      'product-card.price.from': 'Votre prix: ',
      'product-card.price.rrp': '',
    },
  };
  const localeExtras = (lang) => Object.assign({}, CTA[lang], PRICE[lang]);

  const ATC_FONT_FALLBACK = 'Geist, sans-serif';
  const ATC_TOKEN_STYLE_ID = 'fixatech-spark-atc-tokens';
  const ATC_SHADOW_MARK = 'data-fixatech-spark-atc-tokens';
  const ATC_COLOR_PROPS = [
    '--spark-button-color',
    '--spark-button-color-highlight',
    '--spark-button-raised-background',
    '--spark-button-raised-background-hover',
    '--spark-button-raised-hover-background',
    '--spark-button-raised-color',
    '--spark-button-large-color',
    '--spark-button-large-color-highlight',
    '--spark-button-large-background',
    '--spark-button-large-background-hover',
  ];
  const ATC_TYPE = {
    '--spark-button-font-size': '16px',
    '--spark-button-font-weight': '400',
    '--spark-button-large-font-size': '16px',
    '--spark-button-padding': '16px 24px',
    '--spark-button-large-padding': '16px 24px',
    '--spark-button-radius': '12px',
    '--spark-border-radius-button': '12px',
    '--spark-product-card-button-radius': '12px',
    '--spark-product-card-button-padding': '16px 24px',
  };
  /* D2C PDP = scheme-3 primary. All rest-capable tokens = #ffc600.
     Live Spark paints .bg-btn-raised-bg from highlight / secondary / tertiary,
     not raised-background — #e9b217 here becomes the resting fill. */
  const PDP_VARS = Object.assign({}, ATC_TYPE, {
    '--spark-secondary-color': '#ffc600',
    '--spark-tertiary-color': '#ffc600',
    '--spark-button-color': '#ffc600',
    '--spark-button-color-highlight': '#ffc600',
    '--spark-button-raised-background': '#ffc600',
    '--spark-button-raised-background-hover': '#ffc600',
    '--spark-button-raised-hover-background': '#ffc600',
    '--spark-button-raised-color': '#141414',
    '--spark-button-large-color': '#ffc600',
    '--spark-button-large-color-highlight': '#ffc600',
    '--spark-button-large-background': '#ffc600',
    '--spark-button-large-background-hover': '#ffc600',
    '--spark-pricing-fontsize': '32px',
    '--spark-pricing-font-weight': '400',
    '--spark-pricing-rrp-color': '#333333',
  });
  /* D2C collection card = .button.add-to-cart-button in scheme-6 */
  const CARD_VARS = Object.assign({}, ATC_TYPE, {
    '--spark-button-color': '#141414',
    '--spark-button-color-highlight': '#d9d9d9',
    '--spark-button-raised-background': '#141414',
    '--spark-button-raised-background-hover': '#d9d9d9',
    '--spark-button-raised-hover-background': '#d9d9d9',
    '--spark-button-raised-color': '#ffffff',
    '--spark-button-large-color': '#141414',
    '--spark-button-large-color-highlight': '#d9d9d9',
    '--spark-button-large-background': '#141414',
    '--spark-button-large-background-hover': '#d9d9d9',
    '--spark-pricing-font-weight': '700',
    '--spark-pricing-rrp-color': '#333333',
    /* FXT-255: qty-rules chip off. Not the qty stepper. */
    '--spark-product-qty-rules-display': 'none',
  });
  /* D2C assets/icon-add-to-cart.svg — 20×20, stroke currentColor */
  const BAG_ICON_MARK = 'data-fixatech-spark-atc-icon';
  const BAG_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="20" height="20" aria-hidden="true">' +
    '<path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/>' +
    '<path stroke="currentColor" stroke-linecap="round" stroke-width="1.5" d="M14.608 12.563v5m2.5-2.5h-5"/>' +
    '</svg>';
  const ATC_SEL = [
    '.bg-btn-raised-bg',
    '.p-pdp-btn',
    '.fixatech-spark-atc',
    '.fixatech-spark-card-atc',
    '.fixatech-spark-pdp-atc',
    'button.fixatech-spark-atc',
    'button.bg-btn-raised-bg',
    'button.p-pdp-btn',
  ].join(',');
  /* Spark action row = ATC + heart/wishlist/shopping-list twin. Hide the twin. */
  const LIST_MARK = 'data-fixatech-spark-pdp-twin';
  const LIST_ROW_MARK = 'data-fixatech-spark-pdp-atc-row';
  const LIST_WRAP_MARK = 'data-fixatech-spark-pdp-atc-wrap';
  const PDP_STACK_MARK = 'data-fixatech-spark-pdp-atc-stack';
  const CARD_COLUMN_MARK = 'data-fixatech-spark-card-column';
  const CARD_FOOTER_MARK = 'data-fixatech-spark-card-footer';
  const CARD_PIN_MARK = 'data-fixatech-spark-card-pin';
  const CARD_ATC_WRAP_MARK = 'data-fixatech-spark-card-atc-wrap';
  const LIST_ICON_SEL = [
    'svg-heart',
    'svg-heart-solid',
    'svg-heart-outline',
    'svg-heart-fill',
    'svg-heart-regular',
    'svg-heart-empty',
    'svg-solid-heart',
    'svg-outline-heart',
    'svg-favourite',
    'svg-favorite',
    'svg-favorites',
    'svg-favourite-outline',
    'svg-favorite-outline',
    'svg-wishlist',
    'svg-love',
    'svg-clipboard-list',
    'svg-clipboard',
    '[class*="clipboard-list"]',
    '[class*="svg-heart"]',
    '[class*="icon-heart"]',
    '[data-icon="clipboard-list"]',
    '[data-icon="heart"]',
    '[data-icon="wishlist"]',
    '[data-icon="favourite"]',
    '[data-icon="favorite"]',
    '[name="clipboard-list"]',
    '[name="heart"]',
    '[name="wishlist"]',
  ].join(',');
  const TWIN_NAME_RE =
    /clipboard-list|shopping-list|add-to-shopping|wishlist|wish-list|svg-heart|icon-heart|solid-heart|outline-heart|favourite|favorite/;
  const HEART_PATH_RE =
    /M20\.84 4\.61|M4\.318 6\.318|M19 14c1\.49|M21 8\.25c0-2\.485|M12 21\.35|M3\.172 5\.172|4\.688-4\.5|M11\.645 20\.91/;
  const LIST_HINT =
    /shopping\s*lists?|liste\s*d['’]achats?|wishlist|wish\s*list|liste\s*de\s*souhaits?|favoris?|favourites?|favorites?/i;
  const pdpAtcSel = (sel) =>
    sel
      .split(',')
      .map((part) => `${part}:not([${LIST_MARK}]):not(:has(${LIST_ICON_SEL}))`)
      .join(',');
  const PDP_ATC_SEL = pdpAtcSel(ATC_SEL);
  /* Marked outer ATC only — never bare .text-btn / .bg-btn-raised-bg (chip). */
  const CARD_BUTTON_SEL = [
    '.fixatech-spark-card-atc',
    'button.fixatech-spark-card-atc',
    '[role="button"].fixatech-spark-card-atc',
  ].join(',');
  const hoverSel = (sel) =>
    sel
      .split(',')
      .map((part) => `${part}:hover:not(:disabled),${part}.fixatech-spark-atc-hover`)
      .join(',');
  const hoverKids = (sel) => {
    const parents = hoverSel(sel);
    return [' .text-btn', ' .font-btn', ' .text-btn-small', ' .fixatech-spark-atc-label', ' .svg-wrapper.add-to-cart-icon', ' [data-fixatech-spark-atc-icon]', ' svg']
      .map((kid) => parents.split(',').map((p) => `${p}${kid}`).join(','))
      .join(',');
  };
  /* Label + bag only. Never fill inner .bg-btn-raised-bg — that is the chip. */
  const CARD_LABEL_SEL = '.text-btn,.font-btn,.text-btn-small,.fixatech-spark-atc-label';
  const CARD_CHILD_CLEAR =
    `${CARD_LABEL_SEL},.svg-wrapper.add-to-cart-icon,[data-fixatech-spark-atc-icon],.svg-wrapper.add-to-cart-icon svg`;
  const cardDescendantSel = (parents, kids) =>
    parents
      .split(',')
      .flatMap((parent) => kids.split(',').map((kid) => `${parent} ${kid}`))
      .join(',');
  /* D2C .add-to-cart-text: flex + gap; undo Spark icon-only / sr-only. */
  const CARD_LAYOUT_FILL =
    'display:flex!important;flex-flow:row nowrap!important;align-items:center!important;justify-content:center!important;gap:0.3rem!important;' +
    'width:100%!important;max-width:none!important;min-width:0!important;flex:0 0 auto!important;flex-shrink:0!important;' +
    'height:auto!important;min-height:52px!important;max-height:none!important;aspect-ratio:auto!important;' +
    'box-sizing:border-box!important;padding:16px 24px!important;border-radius:12px!important;' +
    'overflow:visible!important;opacity:1!important;visibility:visible!important;' +
    'font-size:16px!important;font-weight:400!important;line-height:normal!important;white-space:nowrap!important;';
  const labelShowDecl = (color) =>
    `display:inline!important;font-size:16px!important;font-weight:400!important;line-height:normal!important;` +
    `letter-spacing:normal!important;color:${color}!important;opacity:1!important;visibility:visible!important;` +
    `background:transparent!important;background-color:transparent!important;box-shadow:none!important;` +
    `position:static!important;width:auto!important;height:auto!important;min-width:0!important;` +
    `max-width:none!important;max-height:none!important;overflow:visible!important;` +
    `clip:auto!important;clip-path:none!important;white-space:nowrap!important;` +
    `text-indent:0!important;transform:none!important;margin:0!important;padding:0!important;border:0!important;`;
  const labelShowCss = (parents, color) =>
    `${cardDescendantSel(parents, CARD_LABEL_SEL)}{${labelShowDecl(color)}}`;
  const childClearCss = (parents, color) =>
    `${cardDescendantSel(parents, CARD_CHILD_CLEAR)}{color:${color}!important;background:transparent!important;background-color:transparent!important;box-shadow:none!important;}`;
  const CHIP_FLAT_SEL = '.bg-btn-raised-bg:not(button):not([role="button"]):not(a),.p-pdp-btn:not(button):not([role="button"]):not(a)';
  const CARD_INNER_CHIP_CLEAR =
    CARD_BUTTON_SEL.split(',')
      .map((p) => `${p} ${CHIP_FLAT_SEL.replace(/,/g, `,${p} `)}`)
      .join(',') +
    '{background:transparent!important;background-color:transparent!important;box-shadow:none!important;display:contents!important;}';
  const ATC_LAYOUT_CSS =
    `${CARD_BUTTON_SEL}{${CARD_LAYOUT_FILL}}` +
    `${CARD_INNER_CHIP_CLEAR}` +
    `${labelShowCss(CARD_BUTTON_SEL, '#ffffff')}` +
    `${CARD_BUTTON_SEL} .svg-wrapper.add-to-cart-icon,${CARD_BUTTON_SEL} [data-fixatech-spark-atc-icon]{display:inline-flex!important;align-items:center!important;justify-content:center!important;width:20px!important;height:20px!important;color:currentColor!important;pointer-events:none!important;flex-shrink:0!important;background:transparent!important;background-color:transparent!important;box-shadow:none!important;border:0!important;}` +
    `${CARD_BUTTON_SEL} .svg-wrapper.add-to-cart-icon svg,${CARD_BUTTON_SEL} [data-fixatech-spark-atc-icon] svg{width:20px!important;height:20px!important;display:block!important;background:transparent!important;}`;
  /* D2C .product-form-buttons button { width:100% } — fill the buy-box. */
  const PDP_ATC_FILL =
    'width:100%!important;max-width:none!important;min-width:0!important;' +
    'flex:1 1 100%!important;box-sizing:border-box!important;align-self:stretch!important;';
  const PDP_FILL_SEL = '.fixatech-spark-pdp-atc,button.fixatech-spark-pdp-atc,[role="button"].fixatech-spark-pdp-atc';
  const ATC_MARK_SEL = '.fixatech-spark-pdp-atc,.fixatech-spark-card-atc';
  const ATC_FILL_SEL = `${PDP_FILL_SEL},${CARD_BUTTON_SEL}`;
  const PDP_FILL_CSS = `${PDP_FILL_SEL}{${PDP_ATC_FILL}}`;
  /* Hard rest fill — beats Spark brand / washed highlight token */
  const PDP_REST_CSS =
    `${PDP_ATC_SEL}{color:#141414!important;background:#ffc600!important;background-color:#ffc600!important;}` +
    `${PDP_ATC_SEL} .text-btn,${PDP_ATC_SEL} .font-btn{color:#141414!important;background:transparent!important;background-color:transparent!important;}`;
  /* Outer button only. Inner raised-bg is flattened via display:contents. */
  const CARD_REST_FILL =
    `${CARD_LAYOUT_FILL}color:#ffffff!important;background:#141414!important;background-color:#141414!important;border-color:#141414!important;` +
    '--spark-button-raised-background:#141414;--spark-button-raised-background-hover:#d9d9d9;' +
    '--spark-button-raised-hover-background:#d9d9d9;--spark-button-color:#141414;' +
    '--spark-button-raised-color:#ffffff;';
  /* scheme-6 .button:hover — whole outer button, not an inner chip */
  const CARD_HOVER_FILL =
    `${CARD_LAYOUT_FILL}color:#141414!important;background:#d9d9d9!important;background-color:#d9d9d9!important;border-color:#d9d9d9!important;` +
    '--spark-button-raised-background:#d9d9d9;--spark-button-raised-background-hover:#d9d9d9;' +
    '--spark-button-raised-hover-background:#d9d9d9;--spark-button-color:#d9d9d9;' +
    '--spark-button-raised-color:#141414;';
  const CARD_REST_CSS =
    `${CARD_BUTTON_SEL}{${CARD_REST_FILL}}` +
    `${CARD_INNER_CHIP_CLEAR}` +
    `${labelShowCss(CARD_BUTTON_SEL, '#ffffff')}` +
    `${childClearCss(CARD_BUTTON_SEL, '#ffffff')}`;
  const CARD_HOVER_CSS =
    `${hoverSel(CARD_BUTTON_SEL)}{${CARD_HOVER_FILL}}` +
    `${labelShowCss(hoverSel(CARD_BUTTON_SEL), '#141414')}` +
    `${hoverKids(CARD_BUTTON_SEL)},${childClearCss(hoverSel(CARD_BUTTON_SEL), '#141414')}`;
  const CARD_HOST_REST_CSS =
    `:host,:host(.fixatech-spark-card-atc){${CARD_REST_FILL}}` +
    `:host ${CHIP_FLAT_SEL.replace(/,/g, ',:host ')}{background:transparent!important;background-color:transparent!important;box-shadow:none!important;display:contents!important;}` +
    `:host .text-btn,:host .font-btn,:host .text-btn-small,:host .fixatech-spark-atc-label{${labelShowDecl('#ffffff')}}` +
    `:host .svg-wrapper.add-to-cart-icon,:host [data-fixatech-spark-atc-icon]{color:#ffffff!important;background:transparent!important;background-color:transparent!important;box-shadow:none!important;}`;
  const CARD_HOST_HOVER_CSS =
    `:host(:hover),:host(.fixatech-spark-atc-hover),:host(.fixatech-spark-card-atc:hover),:host(.fixatech-spark-card-atc.fixatech-spark-atc-hover){${CARD_HOVER_FILL}}` +
    `:host(:hover) .text-btn,:host(:hover) .font-btn,:host(:hover) .text-btn-small,:host(:hover) .fixatech-spark-atc-label,:host(.fixatech-spark-atc-hover) .text-btn,:host(.fixatech-spark-atc-hover) .font-btn,:host(.fixatech-spark-atc-hover) .text-btn-small,:host(.fixatech-spark-atc-hover) .fixatech-spark-atc-label{${labelShowDecl('#141414')}}` +
    `:host(:hover) .svg-wrapper.add-to-cart-icon,:host(:hover) [data-fixatech-spark-atc-icon],:host(.fixatech-spark-atc-hover) .svg-wrapper.add-to-cart-icon,:host(.fixatech-spark-atc-hover) [data-fixatech-spark-atc-icon]{background:transparent!important;background-color:transparent!important;box-shadow:none!important;color:#141414!important;}`;
  /* FXT-254: Horizon mobile is max-width 749px (header / theme standard).
     Hide « au panier » on B2B Spark ATC only. Do not target D2C .add-to-cart-text. */
  const ATC_REST_CLASS = 'fixatech-spark-atc-label__rest';
  const ATC_REST_HIDE = 'display:none!important;';
  const CARD_ATC_MOBILE_LABEL_CSS =
    `@media screen and (max-width: 749px){` +
    `${CARD_BUTTON_SEL} .${ATC_REST_CLASS},` +
    `${PDP_FILL_SEL} .${ATC_REST_CLASS},` +
    `spark-product-card .${ATC_REST_CLASS},` +
    `spark-pdp .${ATC_REST_CLASS}{${ATC_REST_HIDE}}` +
    `}`;
  const ATC_MOBILE_LABEL_SHADOW_CSS =
    `@media screen and (max-width: 749px){.${ATC_REST_CLASS}{${ATC_REST_HIDE}}}`;
  /* scheme-3 .button:hover — yellow darkens via CSS only (vars stay #ffc600) */
  const PDP_HOVER_CSS =
    `${hoverSel(PDP_ATC_SEL)}{color:#141414!important;background:#e9b217!important;background-color:#e9b217!important;}` +
    `${hoverKids(PDP_ATC_SEL)}{color:#141414!important;background:transparent!important;background-color:transparent!important;}`;
  const LIST_BTN_SEL = [
    `button:has(${LIST_ICON_SEL})`,
    `[role="button"]:has(${LIST_ICON_SEL})`,
    `[${LIST_MARK}]`,
    'button[aria-label*="favori" i]',
    'button[aria-label*="wishlist" i]',
    'button[aria-label*="favourite" i]',
    'button[aria-label*="favorite" i]',
    'button[aria-label*="shopping list" i]',
    'button[aria-label*="shopping-list" i]',
    'button[aria-label*="liste d" i]',
    'button[title*="favori" i]',
    'button[title*="wishlist" i]',
    'button[title*="favorite" i]',
  ].join(',');
  /* Hide heart/wishlist/list twin on Spark tiles + theme rows that host them. */
  const LIST_HIDE = 'display:none!important;';
  const LIST_ROW_CSS =
    `display:flex!important;flex-flow:row nowrap!important;align-items:stretch!important;` +
    'width:100%!important;max-width:none!important;box-sizing:border-box!important;';
  /* D2C stacked buy-box: column so ATC is a full-width bar under qty.
     row wrap + flex:1 1 100% still leaves a short pill beside qty. */
  const PDP_STACK_CSS =
    'display:flex!important;flex-direction:column!important;flex-flow:column nowrap!important;' +
    'align-items:stretch!important;width:100%!important;max-width:none!important;box-sizing:border-box!important;';
  const PDP_STACK_ATC =
    'width:100%!important;max-width:none!important;min-width:0!important;' +
    'flex:0 0 auto!important;align-self:stretch!important;box-sizing:border-box!important;';
  const LIST_BTN_HIDE_SEL = `${LIST_BTN_SEL}:not(.fixatech-spark-atc):not(.fixatech-spark-card-atc):not(.fixatech-spark-pdp-atc)`;
  const LIST_CSS =
    `[${LIST_ROW_MARK}]:not([${PDP_STACK_MARK}]){${LIST_ROW_CSS}}` +
    `[${LIST_ROW_MARK}]:not([${PDP_STACK_MARK}]) ${PDP_FILL_SEL}{${PDP_ATC_FILL}}` +
    `[${LIST_WRAP_MARK}]{width:100%!important;max-width:none!important;box-sizing:border-box!important;}` +
    `[${PDP_STACK_MARK}]{${PDP_STACK_CSS}}` +
    `[${PDP_STACK_MARK}] ${PDP_FILL_SEL},[${PDP_STACK_MARK}] [${LIST_WRAP_MARK}]{${PDP_STACK_ATC}}` +
    `${LIST_BTN_HIDE_SEL},[${LIST_ROW_MARK}]>[${LIST_MARK}],[${LIST_ROW_MARK}]>:has([${LIST_MARK}]):not(:has(.fixatech-spark-card-atc)){${LIST_HIDE}}`;
  /* Live Spark: `flex flex-col gap-4 sm:flex-row sm:items-center` + ATC
     `w-full !w-full sm:!w-auto`. sm+ becomes a row and sm:!w-auto
     shrinks the yellow button to leftover (~173px). Beat both. */
  const PDP_SM_ROW_SEL = [
    '.flex.flex-col.sm\\:flex-row',
    '.flex.flex-col.gap-4.sm\\:flex-row',
    '.flex.flex-col.sm\\:flex-row.sm\\:items-center',
    '.sm\\:flex-row:has(.fixatech-spark-pdp-atc)',
    '.sm\\:flex-row:has(.p-pdp-btn)',
    `[${PDP_STACK_MARK}]`,
    `:host([${PDP_STACK_MARK}])`,
  ].join(',');
  const PDP_SM_WAUTO_SEL = [
    PDP_FILL_SEL,
    `${PDP_FILL_SEL}.sm\\:\\!w-auto`,
    '.sm\\:\\!w-auto.fixatech-spark-pdp-atc',
    '.fixatech-spark-pdp-atc.w-full',
    '.fixatech-spark-pdp-atc.\\!w-full',
  ].join(',');
  const PDP_STACK_HAS_CSS =
    `${PDP_SM_ROW_SEL}{${PDP_STACK_CSS}}` +
    `${PDP_SM_WAUTO_SEL}{${PDP_STACK_ATC}}` +
    `@media (min-width:640px){${PDP_SM_ROW_SEL}{${PDP_STACK_CSS}}${PDP_SM_WAUTO_SEL}{${PDP_STACK_ATC}}}`;
  const PRICE_SHADOW_MARK = 'data-fixatech-spark-price';
  const PRICE_LABEL_MARK = 'data-fixatech-spark-price-label';
  const PRICE_SALE_MARK = 'data-fixatech-spark-sale';
  const PRICE_RRP_MARK = 'data-fixatech-spark-rrp';
  const PRICE_STACK_MARK = 'data-fixatech-spark-price-stack';
  const PRICE_SALE_SEL = 'p.font-weight-pricing, .font-weight-pricing';
  const PRICE_RRP_SEL = 'p.sl-pricing-rrp-decoration, .sl-pricing-rrp-decoration';
  const PRICE_SALE_PREFIX = /^(?:votre\s+prix|your\s+price|from)\s*:?\s*/i;
  const PRICE_RRP_PREFIX =
    /^(?:prix\s+r[ée]gulier|prix\s+conseill[ée]|prix\s+recommand[ée]|regular\s+price|recommended(?:\s+retail)?\s+price|suggested(?:\s+retail)?\s+price|rrp|msrp)\s*:?\s*/i;
  /* FXT-247: both PDP amount lines = Flintstock Round / 400. Sizes stay
     32 / 24 (RRP smaller). FXT-248: RRP opacity 0.6; net stays opaque.
     Do not use Geist / 700 here — that is D2C + cards. */
  const PRICE_PDP_FACE =
    "font-family:'Flintstock Round',var(--font-heading--family,sans-serif)!important;font-weight:400!important;";
  const PRICE_STACK_CSS =
    `[${PRICE_STACK_MARK}]{display:flex!important;flex-direction:column!important;align-items:flex-start!important;gap:4px!important;}`;
  const PRICE_LABEL_CSS =
    `[${PRICE_LABEL_MARK}]{font:inherit!important;color:inherit!important;font-weight:inherit!important;text-decoration:none!important;}`;
  const PRICE_CSS =
    `${PRICE_STACK_CSS}` +
    `[${PRICE_SALE_MARK}]{display:block!important;${PRICE_PDP_FACE}` +
    'font-size:32px!important;line-height:32px!important;color:#141414!important;' +
    'text-decoration:none!important;margin:0!important;}' +
    `[${PRICE_RRP_MARK}]{display:block!important;${PRICE_PDP_FACE}` +
    'font-size:24px!important;line-height:32px!important;color:#333333!important;' +
    'margin:0!important;opacity:0.6!important;}' +
    PRICE_LABEL_CSS;
  /* FXT-240: host fills leftover under light-DOM gallery. flex:1 1 0%
     so content (ATC 52px) cannot grow the host past leftover.
     Pin qty+ATC inside that box. overflow:hidden — visible painted
     the button below .product-grid__card. height:100% clipped gallery. */
  const CARD_HOST_FILL =
    'display:flex!important;flex-direction:column!important;align-items:stretch!important;' +
    'width:100%!important;max-width:100%!important;min-width:0!important;' +
    'height:auto!important;min-height:0!important;max-height:100%!important;' +
    'box-sizing:border-box!important;flex:1 1 0%!important;overflow:hidden!important;';
  /* FXT-244: délai trees omit gap-x-5. Growing the host parks leftover
     under ATC (~6–7px). Hug content; light-DOM footer takes margin-top:auto. */
  const CARD_HOST_HUG =
    'display:flex!important;flex-direction:column!important;align-items:stretch!important;' +
    'width:100%!important;max-width:100%!important;min-width:0!important;' +
    'height:auto!important;min-height:0!important;max-height:100%!important;' +
    'box-sizing:border-box!important;flex:0 0 auto!important;overflow:hidden!important;';
  const CARD_DELAY_HOST_CSS =
    ':host-context(product-card:has(.product-inventory__delay-notice)),' +
    ':host-context(.fixatech-card:has(.fixatech-card__stock--delay)){flex:0 0 auto!important;}';
  const CARD_GALLERY_PROTECT =
    'product-card:has(spark-product-card) .card-gallery,' +
    'product-card:has(spark-product-card) .card-gallery slideshow-component,' +
    'product-card:has(spark-product-card) .card-gallery slideshow-container,' +
    'product-card:has(spark-product-card) .card-gallery slideshow-slides{' +
    'flex:0 0 auto!important;flex-shrink:0!important;height:auto!important;' +
    'min-height:auto!important;max-height:none!important;}' +
    'product-card:has(spark-product-card) .card-gallery slideshow-slide,' +
    'product-card:has(spark-product-card) .card-gallery .product-media-container{' +
    'aspect-ratio:var(--gallery-aspect-ratio,var(--media-preview-ratio,1))!important;' +
    'height:auto!important;max-height:none!important;}' +
    '.fixatech-card:has(spark-product-card) .fixatech-card__media{' +
    'flex:0 0 auto!important;flex-shrink:0!important;height:auto!important;' +
    'min-height:auto!important;max-height:none!important;}';
  /* Grow only the marked column, inside the host box. */
  const CARD_SIBLING_CSS =
    'min-width:0!important;max-width:100%!important;box-sizing:border-box!important;';
  const CARD_COLUMN_CSS =
    'display:flex!important;flex-direction:column!important;flex:1 1 0%!important;' +
    'height:auto!important;min-height:0!important;max-height:100%!important;min-width:0!important;max-width:100%!important;' +
    'box-sizing:border-box!important;align-items:stretch!important;overflow:hidden!important;';
  const CARD_FOOTER_CSS =
    'display:flex!important;flex-direction:column!important;flex-flow:column nowrap!important;' +
    'margin-top:auto!important;margin-bottom:0!important;flex:0 0 auto!important;flex-shrink:0!important;' +
    'width:100%!important;max-width:100%!important;height:auto!important;min-height:0!important;' +
    'max-height:none!important;overflow:hidden!important;box-sizing:border-box!important;';
  /* Price/qty/ATC as column siblings (lead-time): auto-margin only — do not
     restyle the price <p> into a flex column. */
  const CARD_PIN_CSS =
    'margin-top:auto!important;margin-bottom:0!important;flex:0 0 auto!important;flex-grow:0!important;' +
    'flex-shrink:0!important;';
  const CARD_ATC_LOCK =
    'display:flex!important;flex:0 0 auto!important;flex-shrink:0!important;' +
    'height:auto!important;min-height:52px!important;max-height:none!important;' +
    'overflow:visible!important;opacity:1!important;visibility:visible!important;' +
    'width:100%!important;box-sizing:border-box!important;position:static!important;';
  const CARD_WRAP_LOCK =
    'flex:0 0 auto!important;flex-grow:0!important;flex-shrink:0!important;' +
    'height:auto!important;min-height:auto!important;max-height:none!important;overflow:visible!important;';
  const CARD_ABOVE_FOOTER =
    `[${CARD_COLUMN_MARK}]>*:not([${CARD_FOOTER_MARK}]):not([${CARD_PIN_MARK}]):not(style){flex-shrink:1!important;min-height:0!important;}`;
  const CARD_FILL_CSS =
    `:host{${CARD_HOST_FILL}}` +
    `${CARD_DELAY_HOST_CSS}` +
    `:host>*:not(style){${CARD_SIBLING_CSS}}` +
    `:host>*:not(style)[${CARD_COLUMN_MARK}],:host>[${CARD_COLUMN_MARK}]{${CARD_COLUMN_CSS}}` +
    `[${CARD_COLUMN_MARK}]{${CARD_COLUMN_CSS}}` +
    `[${CARD_FOOTER_MARK}]{${CARD_FOOTER_CSS}}` +
    `[${CARD_PIN_MARK}]{${CARD_PIN_CSS}}` +
    /* Live Spark class fallback if JS mark is late. */
    `:host>.flex.flex-col>.flex.flex-col.gap-x-5,` +
    `:host>.flex.flex-col>.gap-x-5.gap-y-2{${CARD_FOOTER_CSS}}` +
    /* FXT-244: lead-time trees omit gap-x-5. Price / qty / ATC are column
       siblings. Pin the first commerce child (price). last-child:has(ATC)
       misses when the button IS last — :has is descendants only. */
    `:host>.flex.flex-col>.font-weight-pricing,` +
    `:host>.flex.flex-col>[${PRICE_SALE_MARK}],` +
    `:host>.flex.flex-col>:has(> .font-weight-pricing),` +
    `:host>.flex.flex-col>:has(> [${PRICE_SALE_MARK}]){${CARD_PIN_CSS}}` +
    `${CARD_ABOVE_FOOTER}` +
    `[${CARD_ATC_WRAP_MARK}],[${CARD_FOOTER_MARK}] .grow,[${CARD_FOOTER_MARK}] .min-w-0.grow{${CARD_WRAP_LOCK}}` +
    `${CARD_BUTTON_SEL}{${CARD_ATC_LOCK}}`;
  /* Tiles: weight only. Keep Spark card size. RRP stays muted, not bold. */
  const PRICE_CARD_CSS =
    `${PRICE_STACK_CSS}` +
    `.font-weight-pricing:not(.sl-pricing-rrp-decoration),[${PRICE_SALE_MARK}]{font-weight:700!important;color:#141414!important;text-decoration:none!important;}` +
    `.sl-pricing-rrp-decoration,[${PRICE_RRP_MARK}]{font-weight:400!important;color:#333333!important;}` +
    PRICE_LABEL_CSS;

  const resolveButtonFontFamily = () => {
    const styles = getComputedStyle(document.documentElement);
    const candidates = [
      styles.getPropertyValue('--font-body--family'),
      styles.getPropertyValue('--fixatech-font-body'),
    ];
    for (let i = 0; i < candidates.length; i += 1) {
      const value = (candidates[i] || '').trim();
      if (value && !/flintstock/i.test(value)) return value;
    }
    return ATC_FONT_FALLBACK;
  };

  const isSparkHostName = (name) => name === 'spark-pdp' || name === 'spark-product-card';
  const HOST_SEL = 'spark-pdp, spark-product-card';
  /* Carousel / recommended wrappers that may nest spark-product-card in shadow. */
  const SPARK_WRAPPER_SEL =
    'spark-pdp, spark-product-card, spark-product-recommendations, spark-recommendations, spark-related-products, spark-product-carousel, spark-carousel, spark-products, spark-product-list, spark-upsell';
  /* Horizon late mounts — observe these only, not document.documentElement. */
  const LATE_SEL =
    'product-recommendations, .resource-list, .product-grid, results-list, .fixatech-carousel, .fixatech-card, product-card';

  const collectHosts = (root) => {
    const out = new Set();
    const addFrom = (scope) => {
      if (!scope || !scope.querySelectorAll) return;
      scope.querySelectorAll(HOST_SEL).forEach((h) => out.add(h));
    };
    addFrom(root || document);
    const wrappers = root && root.querySelectorAll ? root : document;
    wrappers.querySelectorAll(SPARK_WRAPPER_SEL).forEach((el) => {
      if (el.shadowRoot) addFrom(el.shadowRoot);
    });
    return out;
  };
  const hasSparkHosts = () => collectHosts().size > 0;
  const isAtcMarked = (el) =>
    Boolean(
      el &&
        el.classList &&
        (el.classList.contains('fixatech-spark-pdp-atc') || el.classList.contains('fixatech-spark-card-atc'))
    );

  const hostKind = (node) => {
    let host = node;
    if (host && host.host) host = host.host;
    while (host) {
      if (host.localName === 'spark-product-card') return 'card';
      if (host.localName === 'spark-pdp') return 'pdp';
      const next = host.getRootNode && host.getRootNode();
      host = next && next.host;
    }
    return null;
  };

  const varsForKind = (kind) => {
    if (kind === 'card') return CARD_VARS;
    if (kind === 'pdp') return PDP_VARS;
    return null;
  };

  const atcDeclarations = (vars) => {
    const font = resolveButtonFontFamily();
    const parts = [`--spark-button-font-family:${font}`];
    Object.keys(vars).forEach((name) => {
      parts.push(`${name}:${vars[name]}`);
    });
    return parts.join(';');
  };

  const writeAtcVars = (el, vars) => {
    if (!el || !el.style || !vars) return;
    const font = resolveButtonFontFamily();
    if (el.style.getPropertyValue('--spark-button-font-family') !== font) {
      el.style.setProperty('--spark-button-font-family', font);
    }
    Object.keys(vars).forEach((name) => {
      const value = vars[name];
      if (el.style.getPropertyValue(name) !== value) {
        el.style.setProperty(name, value);
      }
    });
  };

  const writeSharedType = (el) => {
    if (!el || !el.style) return;
    const font = resolveButtonFontFamily();
    if (el.style.getPropertyValue('--spark-button-font-family') !== font) {
      el.style.setProperty('--spark-button-font-family', font);
    }
    Object.keys(ATC_TYPE).forEach((name) => {
      const value = ATC_TYPE[name];
      if (el.style.getPropertyValue(name) !== value) {
        el.style.setProperty(name, value);
      }
    });
    ATC_COLOR_PROPS.forEach((name) => {
      if (el.style.getPropertyValue(name)) el.style.removeProperty(name);
    });
  };

  const slottedHover = (sel) =>
    sel
      .split(',')
      .map((part) => `:host ::slotted(${part}:hover),:host ::slotted(${part}.fixatech-spark-atc-hover)`)
      .join(',');
  const CARD_SLOTTED_REST_CSS =
    CARD_BUTTON_SEL.split(',')
      .map((part) => `:host ::slotted(${part})`)
      .join(',') + `{${CARD_REST_FILL}}`;
  const CARD_SLOTTED_HOVER_CSS =
    `${slottedHover(CARD_BUTTON_SEL)}{${CARD_HOVER_FILL}}`;

  const hostIsAtcSurface = (root) => {
    const host = root && root.host;
    if (!host || !host.matches) return false;
    if (host.localName === 'spark-product-card' || host.localName === 'spark-pdp') return false;
    return host.matches('.fixatech-spark-card-atc');
  };

  const resolveAtcButton = (el) => {
    if (!el || el.nodeType !== 1) return el;
    let node = el;
    let button = null;
    let fallback = el.localName === 'a' ? null : el;
    while (node && node.nodeType === 1) {
      if (node.localName === 'spark-product-card' || node.localName === 'spark-pdp') break;
      /* Never mark a wrapping PDP <a> as the ATC surface. */
      if (node.localName !== 'a' && node.matches && node.matches('button, [role="button"], .bg-btn-raised-bg, .p-pdp-btn')) {
        if (node.matches('button') && !button) button = node;
        fallback = node;
      }
      const parent = node.parentElement;
      if (parent) {
        node = parent;
        continue;
      }
      const root = node.getRootNode && node.getRootNode();
      const host = root && root.host;
      if (!host || host === node) break;
      node = host;
    }
    if (button) return button;
    if (fallback) return fallback;
    const inner = el.querySelector && el.querySelector('button, [role="button"]:not(a)');
    return inner || el;
  };
  const PDP_SLOTTED_REST_CSS =
    PDP_ATC_SEL.split(',')
      .map((part) => `:host ::slotted(${part})`)
      .join(',') +
    `{${PDP_ATC_FILL}color:#141414!important;background:#ffc600!important;background-color:#ffc600!important;}`;

  const ensureAtcStylesheet = () => {
    const hostCard = CARD_BUTTON_SEL.split(',')
      .map((part) => `spark-product-card ${part}`)
      .join(',');
    const hostHover = CARD_BUTTON_SEL.split(',')
      .map((part) => `spark-product-card ${part}:hover,spark-product-card ${part}.fixatech-spark-atc-hover`)
      .join(',');
    const hostPdp = PDP_ATC_SEL.split(',').map((part) => `spark-pdp ${part}`).join(',');
    const css =
      `spark-pdp{display:block;width:100%;max-width:none;box-sizing:border-box;${atcDeclarations(PDP_VARS)}}` +
      `spark-product-card{${CARD_HOST_FILL}${atcDeclarations(CARD_VARS)}}` +
      `${hostPdp}{${PDP_ATC_FILL}color:#141414!important;background:#ffc600!important;background-color:#ffc600!important;}` +
      `spark-pdp ${PDP_FILL_SEL}{${PDP_ATC_FILL}}` +
      `spark-pdp [${LIST_ROW_MARK}],spark-product-card [${LIST_ROW_MARK}]{${LIST_ROW_CSS}}` +
      `spark-pdp [${LIST_WRAP_MARK}]{${PDP_ATC_FILL}}` +
      `spark-product-card [${LIST_WRAP_MARK}]{width:100%!important;max-width:none!important;flex:0 0 auto!important;height:auto!important;overflow:hidden!important;box-sizing:border-box!important;}` +
      `spark-pdp [${PDP_STACK_MARK}]{${PDP_STACK_CSS}}` +
      `spark-pdp [${PDP_STACK_MARK}] ${PDP_FILL_SEL},spark-pdp [${PDP_STACK_MARK}] [${LIST_WRAP_MARK}]{${PDP_STACK_ATC}}` +
      `spark-pdp [${LIST_MARK}],spark-pdp ${LIST_BTN_HIDE_SEL},spark-product-card [${LIST_MARK}],spark-product-card ${LIST_BTN_HIDE_SEL}{${LIST_HIDE}}` +
      `product-card:has(spark-product-card) ${LIST_BTN_HIDE_SEL},product-card:has(spark-pdp) ${LIST_BTN_HIDE_SEL},product-recommendations:has(spark-product-card) ${LIST_BTN_HIDE_SEL},product-recommendations:has(spark-pdp) ${LIST_BTN_HIDE_SEL},.buy-buttons-block:has(spark-product-card) ${LIST_BTN_HIDE_SEL},.product-form-buttons:has(spark-product-card) ${LIST_BTN_HIDE_SEL}{${LIST_HIDE}}` +
      `product-card:has(spark-product-card) spark-product-card,product-recommendations spark-product-card,.buy-buttons-block spark-product-card,.product-form-buttons spark-product-card,.fixatech-card spark-product-card,.fixatech-carousel__slide spark-product-card{${CARD_HOST_FILL}}` +
      `product-card:has(.product-inventory__delay-notice) spark-product-card,` +
      `.fixatech-card:has(.fixatech-card__stock--delay) spark-product-card,` +
      `product-card:has(.product-inventory__delay-notice) .buy-buttons-block:has(spark-product-card),` +
      `product-card:has(.product-inventory__delay-notice) .product-form-buttons:has(spark-product-card),` +
      `.fixatech-card:has(.fixatech-card__stock--delay) .fixatech-card__spark{${CARD_HOST_HUG}margin-top:auto!important;}` +
      `product-card:has(.product-inventory__delay-notice) .product-card__content>:last-child{margin-top:auto!important;}` +
      `${CARD_GALLERY_PROTECT}` +
      `product-card:has(spark-product-card) add-to-cart-component,product-card:has(spark-product-card) .add-to-cart-button,product-recommendations:has(spark-product-card) .add-to-cart-button,.buy-buttons-block:has([${LIST_MARK}]) .add-to-cart-button{width:100%!important;max-width:none!important;flex:1 1 100%!important;}` +
      `${hostCard}{${CARD_REST_FILL}}` +
      `${labelShowCss(hostCard, '#ffffff')}` +
      `${hostHover}{${CARD_HOVER_FILL}}` +
      `${labelShowCss(hostHover, '#141414')}` +
      /* FXT-242: overlay is a sibling — pointer-events:none so trusted
         clicks hit Spark. Homepage Spark sits in .fixatech-card__spark. */
      `product-card:has(spark-product-card)>.product-card__link,product-card[data-fixatech-spark-card]>.product-card__link{z-index:0;pointer-events:none;}` +
      `product-card:has(spark-product-card)>.product-card__content,product-card[data-fixatech-spark-card]>.product-card__content{position:relative;z-index:1;}` +
      `product-card:has(spark-product-card) spark-product-card,.fixatech-card:has(spark-product-card) spark-product-card,.fixatech-card:has(spark-product-card) .fixatech-card__spark{position:relative;z-index:2;pointer-events:auto;}` +
      `.fixatech-card:has(spark-product-card) .fixatech-card__link,.fixatech-card:has(spark-product-card) .fixatech-card__content,.fixatech-card:has(spark-product-card) .fixatech-card__info{flex:0 0 auto;}` +
      `.fixatech-card:has(spark-product-card) .fixatech-card__actions,.fixatech-card:has(spark-product-card) .fixatech-card__atc-btn,.fixatech-card:has(spark-product-card) .fixatech-card__quantity,.fixatech-card:has(spark-product-card) .fixatech-card__pricing{display:none!important;}` +
      `${CARD_BUTTON_SEL}{pointer-events:auto!important;}` +
      `${CARD_ATC_MOBILE_LABEL_CSS}`;
    let el = document.getElementById(ATC_TOKEN_STYLE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = ATC_TOKEN_STYLE_ID;
    }
    if (el.textContent !== css) el.textContent = css;
    const parent = document.documentElement;
    if (el.parentNode !== parent || parent.lastElementChild !== el) {
      parent.appendChild(el);
    }
  };

  const injectAtcShadow = (root) => {
    if (!root || !root.querySelector) return;
    const kind = hostKind(root);
    const vars = varsForKind(kind);
    if (!vars) return;
    let style = root.querySelector(`style[${ATC_SHADOW_MARK}]`);
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(ATC_SHADOW_MARK, '');
    }
    if (style.parentNode !== root || root.lastChild !== style) root.appendChild(style);
    const extras =
      kind === 'card'
        ? `${root.host && root.host.localName === 'spark-product-card' ? CARD_FILL_CSS : ''}${ATC_LAYOUT_CSS}${CARD_REST_CSS}${CARD_HOVER_CSS}${CARD_SLOTTED_REST_CSS}${CARD_SLOTTED_HOVER_CSS}${LIST_CSS}${hostIsAtcSurface(root) ? `${CARD_HOST_REST_CSS}${CARD_HOST_HOVER_CSS}` : ''}${ATC_MOBILE_LABEL_SHADOW_CSS}`
        : `${PDP_REST_CSS}${PDP_FILL_CSS}${PDP_HOVER_CSS}${PDP_SLOTTED_REST_CSS}${LIST_CSS}${PDP_STACK_HAS_CSS}${ATC_MOBILE_LABEL_SHADOW_CSS}`;
    const hostBox =
      kind === 'pdp' && root.host && root.host.localName === 'spark-pdp'
        ? 'display:block;width:100%;max-width:none;box-sizing:border-box;'
        : kind === 'card' && root.host && root.host.localName === 'spark-product-card'
          ? isDelayCard(root.host)
            ? CARD_HOST_HUG
            : CARD_HOST_FILL
          : '';
    const css = `:host{${hostBox}${atcDeclarations(vars)}}${extras}`;
    if (style.textContent !== css) style.textContent = css;
    if (kind === 'pdp' && root.querySelectorAll) {
      root.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) injectAtcShadow(el.shadowRoot);
      });
    }
  };

  let applyingTokens = false;
  const applyAtcTokens = () => {
    if (applyingTokens) return;
    applyingTokens = true;
    try {
      writeSharedType(document.documentElement);
      if (document.body) writeSharedType(document.body);
      collectHosts().forEach((el) => {
        const kind = hostKind(el);
        if (kind === 'pdp') writeAtcVars(el, PDP_VARS);
        else if (kind === 'card') writeAtcVars(el, CARD_VARS);
      });
      ensureAtcStylesheet();
    } finally {
      applyingTokens = false;
    }
  };

  const watchAtcClobber = () => {
    const mo = new MutationObserver(() => applyAtcTokens());
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
    });
    if (document.head) {
      mo.observe(document.head, { childList: true, subtree: true });
    }
    const watchBody = () => {
      if (document.body) mo.observe(document.body, { childList: true });
    };
    watchBody();
    if (!document.body) document.addEventListener('DOMContentLoaded', watchBody);
  };

  /* Tokens + clobber watch run only after a Spark host is present. */

  const mergeLang = (bucket, extras) => {
    const next = bucket && typeof bucket === 'object' ? bucket : {};
    Object.assign(next, extras);
    return next;
  };

  const mergeTranslations = (opts) => {
    if (!opts || typeof opts !== 'object') return opts;
    const translations = opts.translations && typeof opts.translations === 'object' ? opts.translations : {};
    opts.translations = translations;
    translations.en = mergeLang(translations.en, localeExtras('en'));
    translations.fr = mergeLang(translations.fr, localeExtras('fr'));
    translations['fr-CA'] = mergeLang(translations['fr-CA'], localeExtras('fr'));
    return opts;
  };

  const sealTranslations = (opts) => {
    mergeTranslations(opts);
    if (!opts || typeof opts !== 'object') return;
    let translations = opts.translations;
    try {
      Object.defineProperty(opts, 'translations', {
        configurable: true,
        enumerable: true,
        get() {
          return translations;
        },
        set(value) {
          translations = value && typeof value === 'object' ? value : {};
          translations.en = mergeLang(translations.en, localeExtras('en'));
          translations.fr = mergeLang(translations.fr, localeExtras('fr'));
          translations['fr-CA'] = mergeLang(translations['fr-CA'], localeExtras('fr'));
        },
      });
    } catch (err) {
      /* Spark may have sealed the object; mergeTranslations already ran. */
    }
  };

  const installSparkOptionsHook = () => {
    let current = window.sparkOptions && typeof window.sparkOptions === 'object' ? window.sparkOptions : {};
    sealTranslations(current);

    try {
      Object.defineProperty(window, 'sparkOptions', {
        configurable: true,
        enumerable: true,
        get() {
          return current;
        },
        set(value) {
          current = value && typeof value === 'object' ? value : {};
          sealTranslations(current);
        },
      });
    } catch (err) {
      window.sparkOptions = current;
      sealTranslations(current);
    }

  };

  installSparkOptionsHook();

  const MARK = 'data-fixatech-hide-spark-stock';
  /* Live Pre-order: flex flex-wrap items-center gap-1.5 text-info
     dark:text-dark-mode-status-back-order + svg-history. Tag-agnostic.
     In/out of stock: same layout + svg-solid-success / svg-solid-error. */
  const CSS = [
    '.flex.flex-wrap.items-center.gap-1\\.5.text-info',
    '.flex.flex-wrap.items-center.gap-1\\.5.dark\\:text-dark-mode-status-back-order',
    '.flex.flex-wrap.items-center.gap-1\\.5:has(svg-history)',
    '.flex.flex-wrap.items-center.gap-1\\.5:has(svg-solid-success)',
    '.flex.flex-wrap.items-center.gap-1\\.5:has(svg-solid-error)',
    '.group.flex.items-center.gap-x-1\\.5:has(svg-history)',
    '.group.flex.items-center.gap-x-1\\.5:has(svg-solid-success)',
    '.group.flex.items-center.gap-x-1\\.5:has(svg-solid-error)',
    '.mt-0\\.5.flex.basis-full.flex-wrap.items-center:has(svg-history)',
  ].join(',\n') + ' { display: none !important; }';

  const ROW_SEL = [
    '.flex.flex-wrap.items-center.gap-1\\.5',
    '.group.flex.items-center.gap-x-1\\.5',
    '.mt-0\\.5.flex.basis-full.flex-wrap.items-center',
  ].join(',');
  const ICON_SEL = 'svg-history, svg-solid-success, svg-solid-error';

  const watchingShadow = new WeakSet();
  const waitingHost = new WeakSet();

  const isStockRow = (el) =>
    el.classList.contains('text-info') ||
    el.classList.contains('dark:text-dark-mode-status-back-order') ||
    Boolean(el.querySelector(ICON_SEL));

  const hideRows = (root) => {
    root.querySelectorAll(ROW_SEL).forEach((el) => {
      if (isStockRow(el)) el.style.setProperty('display', 'none', 'important');
    });
  };

  const BUTTON_SEL = 'button, [role="button"], .bg-btn-raised-bg, .p-pdp-btn';
  const PRICE_SUFFIX =
    /\s*\(\s*(?:\{price\}|(?:CA\$|US\$|C\$|\$|€|£)?\s*\d[\d\s.,\u00a0\u202f]*\s*(?:\$|CA\$|C\$|CAD|USD|€|£)?(?:\s*CA)?)\s*\)\s*$/i;
  const ATC_HINT = /add(?: to (?:cart|order))?|ajouter(?:\s+(?:à la commande|au panier))?/i;
  const SPARK_DEFAULT = /^(?:add(?: to order)?|ajouter(?: à la commande)?)$/i;
  const watchingAtc = new WeakSet();
  const waitingAtc = new WeakSet();

  const desiredLabel = () => {
    const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    return lang.indexOf('fr') === 0 ? CTA.fr['pdp.add-to-order.cta-text'] : CTA.en['pdp.add-to-order.cta-text'];
  };

  const AU_PANIER_RE = /\s+au\s+panier/i;

  const unwrapAuPanier = (el) => {
    if (!el || !el.querySelectorAll) return;
    el.querySelectorAll(`.${ATC_REST_CLASS}`).forEach((rest) => {
      const parent = rest.parentNode;
      if (!parent) return;
      parent.replaceChild(document.createTextNode(rest.textContent || ''), rest);
      if (parent.normalize) parent.normalize();
    });
  };

  const ATC_REST_STYLE_MARK = 'data-fixatech-spark-atc-rest';
  const ensureRestStyle = (el) => {
    const root = el.getRootNode && el.getRootNode();
    if (!root || root === document || !root.querySelector) return;
    if (root.querySelector(`style[${ATC_REST_STYLE_MARK}]`)) return;
    const style = document.createElement('style');
    style.setAttribute(ATC_REST_STYLE_MARK, '');
    style.textContent = ATC_MOBILE_LABEL_SHADOW_CSS;
    root.appendChild(style);
  };

  /* FXT-254: keep textContent « Ajouter au panier » so ATC matching stays
     intact; hide the suffix with CSS at 749px. B2B Spark hosts only. */
  const isB2bAtc = (el) =>
    hostKind(el) === 'card' ||
    hostKind(el) === 'pdp' ||
    Boolean(
      el &&
        el.classList &&
        (el.classList.contains('fixatech-spark-card-atc') || el.classList.contains('fixatech-spark-pdp-atc'))
    );

  const wrapAuPanier = (el) => {
    if (!el || !isB2bAtc(el)) return;
    ensureRestStyle(el);
    unwrapAuPanier(el);
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.nodeValue || !AU_PANIER_RE.test(node.nodeValue)) continue;
      const match = node.nodeValue.match(AU_PANIER_RE);
      if (!match) continue;
      const idx = match.index;
      const rest = document.createElement('span');
      rest.className = ATC_REST_CLASS;
      rest.textContent = node.nodeValue.slice(idx, idx + match[0].length);
      const after = node.nodeValue.slice(idx + match[0].length);
      const parent = node.parentNode;
      node.nodeValue = node.nodeValue.slice(0, idx);
      parent.insertBefore(rest, node.nextSibling);
      if (after) parent.insertBefore(document.createTextNode(after), rest.nextSibling);
      return;
    }
  };

  const setLabel = (el, label) => {
    if (
      el.hasAttribute(LIST_MARK) ||
      LIST_HINT.test(el.getAttribute('aria-label') || '') ||
      LIST_HINT.test(el.textContent || '') ||
      (el.querySelector && el.querySelector(LIST_ICON_SEL))
    ) {
      return;
    }
    const rest = el.querySelector && el.querySelector(`.${ATC_REST_CLASS}`);
    if (rest) {
      const combined = (el.textContent || '').replace(/\s+/g, ' ').trim();
      if (combined === label) {
        ensureRestStyle(el);
        return;
      }
      unwrapAuPanier(el);
    }
    const texts = [];
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue && node.nodeValue.trim()) texts.push(node);
    }
    const target =
      texts.find(
        (n) =>
          PRICE_SUFFIX.test(n.nodeValue) ||
          ATC_HINT.test(n.nodeValue) ||
          SPARK_DEFAULT.test(n.nodeValue.trim()) ||
          n.nodeValue.replace(/\s+/g, ' ').trim() === label
      ) || (texts.length === 1 ? texts[0] : null);
    if (target) {
      const current = target.nodeValue;
      const next = current.replace(current.trim(), label);
      if (next !== current) target.nodeValue = next;
      wrapAuPanier(el);
      return;
    }
    /* Never assign el.textContent — that destroys the bag / Spark label node. */
    let span = el.querySelector('.fixatech-spark-atc-label');
    if (!span) {
      span = document.createElement('span');
      span.className = 'text-btn font-btn fixatech-spark-atc-label';
      el.appendChild(span);
    }
    if (span.textContent !== label) span.textContent = label;
    wrapAuPanier(el);
  };

  const clearChildFills = (root, hover) => {
    if (!root || !root.querySelectorAll) return;
    const color = hover ? '#141414' : '#ffffff';
    root.querySelectorAll('.svg-wrapper.add-to-cart-icon, [data-fixatech-spark-atc-icon], .text-btn, .font-btn, .text-btn-small, .fixatech-spark-atc-label').forEach((node) => {
      node.style.setProperty('background', 'transparent', 'important');
      node.style.setProperty('background-color', 'transparent', 'important');
      node.style.setProperty('box-shadow', 'none', 'important');
      node.style.setProperty('color', color, 'important');
      if (node.matches('.text-btn, .font-btn, .text-btn-small, .fixatech-spark-atc-label')) {
        node.style.setProperty('display', 'inline', 'important');
        node.style.setProperty('font-size', '16px', 'important');
        node.style.setProperty('font-weight', '400', 'important');
        node.style.setProperty('opacity', '1', 'important');
        node.style.setProperty('visibility', 'visible', 'important');
        node.style.setProperty('overflow', 'visible', 'important');
        node.style.setProperty('position', 'static', 'important');
        node.style.setProperty('width', 'auto', 'important');
        node.style.setProperty('height', 'auto', 'important');
        node.style.setProperty('clip-path', 'none', 'important');
        node.style.setProperty('white-space', 'nowrap', 'important');
      }
    });
    root.querySelectorAll('.bg-btn-raised-bg, .p-pdp-btn').forEach((node) => {
      if (node === root) return;
      if (node.matches && node.matches('button, [role="button"], a')) return;
      node.style.setProperty('background', 'transparent', 'important');
      node.style.setProperty('background-color', 'transparent', 'important');
      node.style.setProperty('display', 'contents', 'important');
    });
  };

  const ensureBagIcon = (el) => {
    if (el.querySelector(`[${BAG_ICON_MARK}]`)) {
      clearChildFills(el, false);
      return;
    }
    if (el.querySelector('svg, .add-to-cart-icon, svg-wrapper')) return;
    const wrap = document.createElement('span');
    wrap.className = 'svg-wrapper add-to-cart-icon';
    wrap.setAttribute(BAG_ICON_MARK, '');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.setProperty('background', 'transparent', 'important');
    wrap.style.setProperty('background-color', 'transparent', 'important');
    wrap.style.setProperty('box-shadow', 'none', 'important');
    wrap.innerHTML = BAG_SVG;
    el.insertBefore(wrap, el.firstChild);
  };

  const removeBagIcon = (el) => {
    el.querySelectorAll(`[${BAG_ICON_MARK}]`).forEach((node) => node.remove());
  };

  const paintCardSurface = (node, hover) => {
    if (!node || !node.style) return;
    const bg = hover ? '#d9d9d9' : '#141414';
    const fg = hover ? '#141414' : '#ffffff';
    if (hover) node.classList.add('fixatech-spark-atc-hover');
    else node.classList.remove('fixatech-spark-atc-hover');
    node.style.setProperty('background', bg, 'important');
    node.style.setProperty('background-color', bg, 'important');
    node.style.setProperty('color', fg, 'important');
    node.style.setProperty('border-color', bg, 'important');
    node.style.setProperty('width', '100%', 'important');
    node.style.setProperty('max-width', 'none', 'important');
    node.style.setProperty('min-width', '0', 'important');
    node.style.setProperty('flex', '0 0 auto', 'important');
    node.style.setProperty('flex-shrink', '0', 'important');
    node.style.setProperty('box-sizing', 'border-box', 'important');
    node.style.setProperty('align-self', 'stretch', 'important');
    node.style.setProperty('padding', '16px 24px', 'important');
    node.style.setProperty('border-radius', '12px', 'important');
    node.style.setProperty('overflow', 'visible', 'important');
    node.style.setProperty('opacity', '1', 'important');
    node.style.setProperty('visibility', 'visible', 'important');
    node.style.setProperty('height', 'auto', 'important');
    node.style.setProperty('min-height', '52px', 'important');
    node.style.setProperty('max-height', 'none', 'important');
    node.style.setProperty('font-size', '16px', 'important');
    node.style.setProperty('font-weight', '400', 'important');
    node.style.setProperty('display', 'flex', 'important');
    node.style.setProperty('flex-flow', 'row nowrap', 'important');
    node.style.setProperty('align-items', 'center', 'important');
    node.style.setProperty('justify-content', 'center', 'important');
    node.style.setProperty('gap', '0.3rem', 'important');
    node.style.setProperty('--spark-button-raised-background', bg);
    node.style.setProperty('--spark-button-raised-background-hover', '#d9d9d9');
    node.style.setProperty('--spark-button-raised-hover-background', '#d9d9d9');
    node.style.setProperty('--spark-button-color', bg);
    node.style.setProperty('--spark-button-raised-color', fg);
  };

  const cardPaintTargets = (root) => {
    const out = [];
    if (root && root.nodeType === 1 && root.style) out.push(root);
    return out;
  };

  const paintCardTargets = (root, hover) => {
    cardPaintTargets(root).forEach((node) => paintCardSurface(node, hover));
  };

  const bindCardHover = (el, surface) => {
    const root = surface && surface !== el ? surface : el;
    if (el.getAttribute('data-fixatech-spark-atc-hover') === '1') {
      const hovering = root.classList && root.classList.contains('fixatech-spark-atc-hover');
      paintCardTargets(root, hovering);
      clearChildFills(root, hovering);
      return;
    }
    el.setAttribute('data-fixatech-spark-atc-hover', '1');
    if (root !== el) root.setAttribute('data-fixatech-spark-atc-hover', '1');
    paintCardTargets(root, false);
    clearChildFills(root, false);
    const on = () => {
      paintCardTargets(root, true);
      clearChildFills(root, true);
    };
    const off = () => {
      paintCardTargets(root, false);
      clearChildFills(root, false);
    };
    root.addEventListener('pointerenter', on);
    root.addEventListener('pointerleave', off);
  };

  const collectSvgs = (root, out) => {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('svg, path').forEach((n) => out.push(n));
    if (root.shadowRoot) collectSvgs(root.shadowRoot, out);
    root.querySelectorAll('*').forEach((n) => {
      if (n.shadowRoot) collectSvgs(n.shadowRoot, out);
    });
  };

  const hasTwinIcon = (el) => {
    if (!el || el.nodeType !== 1) return false;
    if (el.matches && el.matches(LIST_ICON_SEL)) return true;
    if (el.localName && TWIN_NAME_RE.test(el.localName)) return true;
    if (queryDeep(el, LIST_ICON_SEL)) return true;
    const svgs = [];
    collectSvgs(el, svgs);
    return svgs.some((n) => {
      const name = [
        n.localName,
        n.getAttribute('class') || '',
        n.getAttribute('id') || '',
        n.getAttribute('aria-label') || '',
        n.getAttribute('data-icon') || '',
        n.getAttribute('name') || '',
      ].join(' ');
      if (TWIN_NAME_RE.test(name)) return true;
      return HEART_PATH_RE.test(n.getAttribute('d') || '');
    });
  };

  const isQtyControl = (el) => {
    if (!el || el.nodeType !== 1) return false;
    if (el.matches && el.matches('input, select, [type="number"], [role="spinbutton"]')) return true;
    const aria = `${(el.getAttribute && el.getAttribute('aria-label')) || ''} ${(el.getAttribute && el.getAttribute('name')) || ''}`;
    if (/quantity|qty|increase|decrease|augmenter|diminuer/i.test(aria)) return true;
    const cls = typeof el.className === 'string' ? el.className : (el.className && el.className.baseVal) || '';
    if (/qty|quantity|min-w-36|max-w-36|\bw-36\b/i.test(cls)) return true;
    const raw = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return /^[+\-−–]$/.test(raw);
  };

  /* FXT-242: live ATC path is div×5 → #document-fragment →
     spark-product-card (no <a>, no <button>). Capture-phase
     stopPropagation so Horizon never navigateToProduct; add via Spark. */
  const composedPathOf = (event) =>
    typeof event.composedPath === 'function' ? event.composedPath() : [event.target];

  const ATC_CHROME_SEL = [
    'button',
    '[role="button"]',
    '.fixatech-spark-card-atc',
    '.fixatech-spark-atc',
    '.bg-btn-raised-bg',
    '.p-pdp-btn',
    '.text-btn',
    '.font-btn',
    '.text-btn-small',
    '.fixatech-spark-atc-label',
    '.svg-wrapper.add-to-cart-icon',
    '[data-fixatech-spark-atc-icon]',
    `[${CARD_ATC_WRAP_MARK}]`,
  ].join(',');
  const ATC_FILL_RE = /^rgb\(\s*(?:20\s*,\s*20\s*,\s*20|217\s*,\s*217\s*,\s*217)\s*\)$/;
  const PRICE_ONLY_RE = /votre prix|your price|prix régulier|regular price/i;

  const nodeAtcLabel = (el) => {
    if (!el) return false;
    const raw = ((el.textContent || '') + '').replace(/\s+/g, ' ').trim();
    if (!raw || raw.length > 48) return false;
    return ATC_HINT.test(raw) || raw === desiredLabel() || SPARK_DEFAULT.test(raw);
  };

  const nodeAtcFill = (el) => {
    if (!el || typeof getComputedStyle !== 'function') return false;
    const h = el.offsetHeight;
    if (!(h > 0 && h <= 80)) return false;
    return ATC_FILL_RE.test(getComputedStyle(el).backgroundColor);
  };

  const isPriceNode = (el) => {
    if (!el || el.nodeType !== 1) return false;
    const raw = ((el.textContent || '') + '').replace(/\s+/g, ' ').trim();
    if (!raw || raw.length > 64 || ATC_HINT.test(raw)) return false;
    return PRICE_ONLY_RE.test(raw) || /^[\$€£]|CA\$|US\$/.test(raw);
  };

  const isAtcChromeNode = (el) => {
    if (!el || el.nodeType !== 1) return false;
    if (el.localName === 'spark-product-card' || el.localName === 'spark-pdp') return false;
    if (
      el.hasAttribute &&
      (el.hasAttribute(CARD_COLUMN_MARK) || el.hasAttribute(CARD_FOOTER_MARK) || el.hasAttribute(CARD_PIN_MARK))
    ) {
      return false;
    }
    if (isQtyControl(el) || isPriceNode(el)) return false;
    if (el.matches && el.matches(ATC_CHROME_SEL)) return true;
    if (isAtcMarked(el)) return true;
    if (nodeAtcLabel(el)) return true;
    return nodeAtcFill(el);
  };

  const lightCardFrom = (host) => {
    let n = host;
    let depth = 0;
    while (n && depth < 24) {
      if (n.nodeType === 1) {
        if (n.localName === 'product-card') return n;
        if (n.classList && n.classList.contains('fixatech-card')) return n;
      }
      if (n.parentElement) {
        n = n.parentElement;
        depth += 1;
        continue;
      }
      const root = n.getRootNode && n.getRootNode();
      const next = root && root.host;
      if (!next || next === n) break;
      n = next;
      depth += 1;
    }
    return null;
  };

  const isDelayCard = (host) => {
    const card =
      host &&
      (host.localName === 'product-card' || (host.classList && host.classList.contains('fixatech-card')))
        ? host
        : lightCardFrom(host);
    if (!card || !card.querySelector) return false;
    return Boolean(card.querySelector('.product-inventory__delay-notice, .fixatech-card__stock--delay'));
  };

  const pinDelayLightFooter = (spark) => {
    const card = lightCardFrom(spark);
    if (!card) return;
    const wrap = (spark.closest && spark.closest('.buy-buttons-block, .product-form-buttons, .fixatech-card__spark')) || null;
    if (wrap) {
      wrap.style.setProperty('flex', '0 0 auto', 'important');
      wrap.style.setProperty('height', 'auto', 'important');
      wrap.style.setProperty('min-height', '0', 'important');
      wrap.style.setProperty('margin-top', 'auto', 'important');
    }
    const content = card.querySelector && card.querySelector('.product-card__content');
    const last = content && content.lastElementChild;
    if (last) last.style.setProperty('margin-top', 'auto', 'important');
  };

  const firstAttr = (el, names) => {
    if (!el || !el.getAttribute) return '';
    for (let i = 0; i < names.length; i += 1) {
      const v = (el.getAttribute(names[i]) || '').trim();
      if (v) return v;
    }
    return '';
  };

  const SKU_ATTRS = ['data-fixatech-spark-sku', 'sku', 'data-sku', 'product-sku', 'data-product-sku'];

  const skuFromText = (el) => {
    if (!el) return '';
    const text = ((el.textContent || el.innerText || '') + '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    /* Live bag line: MIL8 3697-25 */
    const branded = text.match(/\b[A-Z]{2,}\d*\s+\d{3,5}[-/][A-Z0-9]{1,6}\b/);
    if (branded) return branded[0].trim();
    const code = text.match(/\b[A-Z0-9]{2,}[-_][A-Z0-9][-A-Z0-9]{1,}\b/);
    return code ? code[0].trim() : '';
  };

  const skuFromHost = (host) => {
    if (!host) return '';
    const fromProp = host.sku || (host.product && host.product.sku) || '';
    if (fromProp) return String(fromProp).trim();
    const fromHost = firstAttr(host, SKU_ATTRS);
    if (fromHost) return fromHost;
    const card = lightCardFrom(host);
    const fromCard = firstAttr(card, ['data-sku', 'data-fixatech-sku', 'sku']);
    if (fromCard) return fromCard;
    const root = host.shadowRoot;
    if (root && root.querySelector) {
      const marked = root.querySelector('[data-sku], [sku], [data-product-sku]');
      const deep = firstAttr(marked, SKU_ATTRS);
      if (deep) return deep;
      const fromShadow = skuFromText(root);
      if (fromShadow) return fromShadow;
    }
    if (card) {
      const barcode = card.querySelector && card.querySelector('.barcode, [data-testid="product-barcode"]');
      const fromBarcode = skuFromText(barcode) || firstAttr(barcode, ['data-sku']);
      if (fromBarcode) return fromBarcode;
      const fromCardText = skuFromText(card);
      if (fromCardText) return fromCardText;
    }
    return skuFromText(host);
  };

  const qtyFromHost = (host) => {
    if (!host) return 1;
    const scan = (root) => {
      if (!root || !root.querySelector) return 0;
      const input = root.querySelector('input[type="number"], [role="spinbutton"]');
      if (!input) return 0;
      const n = parseInt(input.value, 10);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };
    return scan(host.shadowRoot) || scan(host) || 1;
  };

  const onSparkVariantChange = (event) => {
    const host = event.currentTarget;
    const d = (event && (event.data || event.detail)) || {};
    const sku = d.sku || d.SKU || (d.variant && d.variant.sku) || '';
    if (sku && host && host.setAttribute) host.setAttribute('data-fixatech-spark-sku', sku);
  };

  /* Official Spark add — synthetic click is untrusted and does not fire. */
  const addViaSparkUpdateCart = (host) => {
    if (!host || host.localName !== 'spark-product-card' || host.__fixatechSparkAdding) return false;
    markSparkCard(host);
    const sku = skuFromHost(host);
    const spark = window.spark;
    if (!sku || !spark || typeof spark.updateCart !== 'function') return false;
    const qty = qtyFromHost(host);
    host.__fixatechSparkAdding = true;
    Promise.resolve(spark.updateCart({ products: [{ sku, adjustQuantity: qty }] }))
      .catch(() => {})
      .then(() => {
        host.__fixatechSparkAdding = false;
      });
    return true;
  };

  const markSparkCard = (host) => {
    if (!host || host.localName !== 'spark-product-card') return;
    const card = lightCardFrom(host);
    if (!card) return;
    card.setAttribute('data-fixatech-spark-card', '');
    if (!firstAttr(host, SKU_ATTRS)) {
      const sku = firstAttr(card, ['data-sku', 'data-fixatech-sku', 'sku']);
      if (sku) host.setAttribute('data-fixatech-spark-sku', sku);
    }
  };

  const boundCardNav = new WeakSet();
  const bindCardNavGuard = (host) => {
    if (!host || host.localName !== 'spark-product-card') return;
    if (host.closest('spark-pdp')) return;
    markSparkCard(host);
    if (boundCardNav.has(host)) return;
    boundCardNav.add(host);
    host.addEventListener('spark-variant-change', onSparkVariantChange);
  };

  const onDocumentSparkCardNavCapture = (event) => {
    const path = composedPathOf(event);
    let sparkHost = null;
    let qty = false;
    let chrome = false;
    let price = false;
    for (let i = 0; i < path.length; i += 1) {
      const n = path[i];
      if (!n || n.nodeType !== 1) continue;
      if (n.localName === 'spark-pdp') return;
      if (n.localName === 'spark-product-card') {
        sparkHost = n;
        break;
      }
      if (isQtyControl(n)) qty = true;
      else if (isPriceNode(n)) price = true;
      else if (isAtcChromeNode(n)) chrome = true;
    }
    if (!sparkHost || qty || price) return;
    /* RCA: div×5 → #document-fragment → spark-product-card is the black Ajouter area. */
    if (!chrome) {
      chrome = true;
      for (let i = 0; i < path.length; i += 1) {
        const n = path[i];
        if (!n || n.nodeType !== 1) continue;
        if (n.localName === 'spark-product-card') break;
        const name = n.localName;
        if (name !== 'div' && name !== 'span' && name !== 'svg' && name !== 'path') {
          chrome = false;
          break;
        }
      }
    }
    if (!chrome) return;
    event.stopPropagation();
    addViaSparkUpdateCart(sparkHost);
  };

  const childLooksQty = (el) => {
    if (!el || el.nodeType !== 1) return false;
    if (isQtyControl(el)) return true;
    const name = el.localName || '';
    if (/qty|quantity/i.test(name)) return true;
    const cls = typeof el.className === 'string' ? el.className : '';
    if (/qty|quantity|min-w-36|max-w-36|\bw-36\b/i.test(cls)) return true;
    const qtyHit = 'input[type="number"], [aria-label*="quantity" i], [aria-label*="qty" i]';
    if (el.querySelector && el.querySelector(qtyHit)) return true;
    const scan = (root) => {
      if (!root || !root.querySelector) return false;
      if (root.querySelector(qtyHit)) return true;
      if (root.shadowRoot && scan(root.shadowRoot)) return true;
      if (!root.querySelectorAll) return false;
      const deep = root.querySelectorAll('*');
      for (let i = 0; i < deep.length; i += 1) {
        if (deep[i].shadowRoot && scan(deep[i].shadowRoot)) return true;
      }
      return false;
    };
    return scan(el);
  };

  const nodeHasQty = (node) => {
    if (!node || node.nodeType !== 1) return false;
    if (childLooksQty(node)) return true;
    const kids = node.children ? Array.from(node.children) : [];
    return kids.some((kid) => childLooksQty(kid));
  };

  const applyPdpAtcStack = (node) => {
    if (!node || node.nodeType !== 1) return;
    node.setAttribute(PDP_STACK_MARK, '');
    node.removeAttribute(LIST_ROW_MARK);
    if (node.classList) {
      node.classList.remove('sm:flex-row');
      node.classList.add('sm:flex-col');
    }
    if (!node.style) return;
    node.style.setProperty('display', 'flex', 'important');
    node.style.setProperty('flex-direction', 'column', 'important');
    node.style.setProperty('flex-wrap', 'nowrap', 'important');
    node.style.setProperty('align-items', 'stretch', 'important');
    node.style.setProperty('width', '100%', 'important');
    node.style.setProperty('max-width', 'none', 'important');
    node.style.setProperty('box-sizing', 'border-box', 'important');
  };

  const isShoppingListControl = (el) => {
    if (!el || el.nodeType !== 1) return false;
    if (el.hasAttribute && el.hasAttribute(LIST_MARK)) return true;
    const cls = typeof el.className === 'string' ? el.className : (el.className && el.className.baseVal) || '';
    if (TWIN_NAME_RE.test(cls)) return true;
    if (el.localName && TWIN_NAME_RE.test(el.localName)) return true;
    const aria = `${(el.getAttribute && el.getAttribute('aria-label')) || ''} ${(el.getAttribute && el.getAttribute('title')) || ''}`;
    if (LIST_HINT.test(aria)) return true;
    if (LIST_HINT.test((el.textContent || '').replace(/\s+/g, ' '))) return true;
    return hasTwinIcon(el);
  };

  const nextAncestor = (node) => {
    if (!node || node.nodeType !== 1) return null;
    if (node.parentElement) return node.parentElement;
    const root = node.getRootNode && node.getRootNode();
    const host = root && root.host;
    return host && host !== node ? host : null;
  };

  const queryDeep = (node, sel) => {
    if (!node) return null;
    if (node.querySelector) {
      const hit = node.querySelector(sel);
      if (hit) return hit;
    }
    if (node.shadowRoot) {
      const fromSelf = queryDeep(node.shadowRoot, sel);
      if (fromSelf) return fromSelf;
    }
    if (!node.querySelectorAll) return null;
    const kids = node.querySelectorAll('*');
    for (let i = 0; i < kids.length; i += 1) {
      if (!kids[i].shadowRoot) continue;
      const nested = queryDeep(kids[i].shadowRoot, sel);
      if (nested) return nested;
    }
    return null;
  };

  const collectDeep = (root, sel, out) => {
    if (!root || !root.querySelectorAll) return out;
    root.querySelectorAll(sel).forEach((el) => out.push(el));
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) collectDeep(el.shadowRoot, sel, out);
    });
    return out;
  };

  /* FXT-240: yellow-W live shadow — image/title are light DOM.
     Mark :host > div.flex.flex-col.gap-y-2 as the column.
     Mark the qty+ATC flex-col (parent of .min-w-0.grow), never the button. */
  const pinSparkCardFooter = (root) => {
    if (!root || !root.host || root.host.localName !== 'spark-product-card') return;
    const columnKids = (node) => {
      if (!node || !node.children) return [];
      return Array.from(node.children).filter((n) => n.nodeType === 1 && n.localName !== 'style');
    };
    const classNameOf = (el) => {
      if (!el) return '';
      return typeof el.className === 'string' ? el.className : (el.className && el.className.baseVal) || '';
    };
    if (root.querySelectorAll) {
      root.querySelectorAll(`[${CARD_COLUMN_MARK}]`).forEach((el) => el.removeAttribute(CARD_COLUMN_MARK));
      root.querySelectorAll(`[${CARD_FOOTER_MARK}]`).forEach((el) => el.removeAttribute(CARD_FOOTER_MARK));
      root.querySelectorAll(`[${CARD_PIN_MARK}]`).forEach((el) => el.removeAttribute(CARD_PIN_MARK));
      root.querySelectorAll(`[${CARD_ATC_WRAP_MARK}]`).forEach((el) => el.removeAttribute(CARD_ATC_WRAP_MARK));
    }

    const column = columnKids(root)[0];
    if (!column || column.nodeType !== 1) return;
    column.setAttribute(CARD_COLUMN_MARK, '');

    const atcs = collectDeep(root, '.fixatech-spark-card-atc, .bg-btn-raised-bg, .p-pdp-btn', []);
    const atc =
      atcs.find((el) => isAtcMarked(el)) ||
      atcs.find((el) => el && !isQtyControl(el) && !isShoppingListControl(el)) ||
      null;
    if (!atc) return;

    const isGrowOnlyWrap = (el) => {
      if (!el || el === column || el === atc) return false;
      const cls = classNameOf(el);
      if (!/\bgrow\b/.test(cls) && !/\bmin-w-0\b/.test(cls)) return false;
      return Boolean(el.contains && el.contains(atc) && !nodeHasQty(el));
    };
    const isControlsGroup = (el) => {
      if (!el || el === column || el === atc || isSparkHostName(el.localName)) return false;
      if (isGrowOnlyWrap(el)) return false;
      const cls = classNameOf(el);
      if (/\bflex-col\b/.test(cls) && /\bgap-x-5\b/.test(cls)) return true;
      return Boolean(el.contains && el.contains(atc) && nodeHasQty(el));
    };
    const looksPrice = (el) => {
      if (!el || el.nodeType !== 1 || el === column) return false;
      if (isPriceNode(el)) return true;
      if (el.hasAttribute && (el.hasAttribute(PRICE_SALE_MARK) || el.hasAttribute(PRICE_RRP_MARK))) return true;
      const cls = classNameOf(el);
      if (/\bfont-weight-pricing\b/.test(cls) || /\bsl-pricing-rrp/.test(cls)) return true;
      return Boolean(
        el.querySelector &&
          el.querySelector(
            `.font-weight-pricing, .sl-pricing-rrp-decoration, [${PRICE_SALE_MARK}], [${PRICE_RRP_MARK}]`
          )
      );
    };
    const isCommerceKid = (el) => {
      if (!el || el === column) return false;
      if (el === atc) return true;
      if (el.contains && el.contains(atc)) return true;
      return looksPrice(el) || nodeHasQty(el);
    };

    const kids = columnKids(column);
    let footer = kids.find((k) => isControlsGroup(k));
    if (!footer) {
      footer = kids.find((k) => k !== atc && k.contains && k.contains(atc) && !isGrowOnlyWrap(k));
    }
    if (!footer) {
      let node = atc.parentElement;
      while (node && node !== column && !isSparkHostName(node.localName)) {
        const parent = node.parentElement;
        if (parent === column && !isGrowOnlyWrap(node)) {
          footer = node;
          break;
        }
        if (isControlsGroup(node)) footer = node;
        node = parent;
      }
    }
    if (footer && (footer === atc || isGrowOnlyWrap(footer))) {
      const parent = footer.parentElement;
      footer = parent && parent !== column && isControlsGroup(parent) ? parent : null;
    }
    /* Lead-time: price, qty, ATC are siblings — no gap-x-5 wrapper. Pin the
       first commerce child so leftover sits above Votre prix, not under ATC. */
    if (!footer) {
      footer = kids.find((k) => k !== atc && looksPrice(k)) || kids.find((k) => k !== atc && isCommerceKid(k));
    }
    if (footer && footer !== column && !isSparkHostName(footer.localName)) {
      const pinOnly =
        footer === atc ||
        isGrowOnlyWrap(footer) ||
        (looksPrice(footer) && !nodeHasQty(footer) && !(footer.contains && footer.contains(atc)));
      if (pinOnly) {
        footer.setAttribute(CARD_PIN_MARK, '');
      } else if (footer !== atc) {
        footer.setAttribute(CARD_FOOTER_MARK, '');
      }
    }
    let wrap = atc.parentElement;
    while (wrap && wrap !== column && !isSparkHostName(wrap.localName)) {
      if (isGrowOnlyWrap(wrap)) wrap.setAttribute(CARD_ATC_WRAP_MARK, '');
      wrap = wrap.parentElement;
    }
  };

  const nodeHasList = (node) => {
    if (!node || node.nodeType !== 1) return false;
    if (node.hasAttribute && node.hasAttribute(LIST_MARK)) return true;
    return Boolean(queryDeep(node, `[${LIST_MARK}], ${LIST_ICON_SEL}`));
  };

  const nodeHasAtc = (node, atc) => {
    if (!node || node.nodeType !== 1) return false;
    if (atc && atc.nodeType === 1 && isAtcMarked(atc) && node.contains && node.contains(atc)) {
      return true;
    }
    return Boolean(queryDeep(node, ATC_MARK_SEL));
  };

  const applyPdpAtcFill = (el) => {
    if (!el || !el.style || isShoppingListControl(el)) return;
    const stacked = hostKind(el) === 'pdp';
    if (stacked && el.classList) {
      el.classList.remove('sm:!w-auto');
      el.classList.add('w-full', '!w-full');
    }
    const isCard = hostKind(el) === 'card';
    el.style.setProperty('width', '100%', 'important');
    el.style.setProperty('max-width', 'none', 'important');
    el.style.setProperty('min-width', stacked ? '100%' : '0', 'important');
    el.style.setProperty('flex', stacked || isCard ? '0 0 auto' : '1 1 100%', 'important');
    el.style.setProperty('flex-shrink', stacked || isCard ? '0' : '1', 'important');
    el.style.setProperty('box-sizing', 'border-box', 'important');
    el.style.setProperty('align-self', 'stretch', 'important');
    if (isCard) {
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('min-height', '52px', 'important');
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('overflow', 'visible', 'important');
      el.style.setProperty('opacity', '1', 'important');
      el.style.setProperty('visibility', 'visible', 'important');
      el.style.setProperty('display', 'flex', 'important');
    }
  };

  /* Mark the ATC+twin row (not the Spark host). Stretch only ATC-side wrappers.
     spark-pdp qty+ATC is a column (D2C stacked), never a nowrap twin row. */
  const markAtcListRow = (fromEl) => {
    if (!fromEl || !hostKind(fromEl)) return;
    const isPdp = hostKind(fromEl) === 'pdp';
    const wraps = [];
    let node = nextAncestor(fromEl);
    let depth = 0;
    while (node && node.nodeType === 1 && !isSparkHostName(node.localName) && depth < 8) {
      const childCount = node.children ? node.children.length : 0;
      if (nodeHasAtc(node, fromEl) && childCount >= 2 && childCount <= 4) {
        if (isPdp && nodeHasQty(node)) {
          applyPdpAtcStack(node);
          if (!isShoppingListControl(fromEl) && !(fromEl.hasAttribute && fromEl.hasAttribute(LIST_MARK))) {
            wraps.forEach((wrap) => {
              if (
                wrap !== node &&
                !isSparkHostName(wrap.localName) &&
                !wrap.hasAttribute(LIST_MARK) &&
                !isShoppingListControl(wrap)
              ) {
                wrap.setAttribute(LIST_WRAP_MARK, '');
                applyPdpAtcFill(wrap);
              }
            });
          }
          return;
        }
        if (nodeHasList(node)) {
          node.setAttribute(LIST_ROW_MARK, '');
          if (!isShoppingListControl(fromEl) && !(fromEl.hasAttribute && fromEl.hasAttribute(LIST_MARK))) {
            wraps.forEach((wrap) => {
              if (
                wrap !== node &&
                !isSparkHostName(wrap.localName) &&
                !wrap.hasAttribute(LIST_MARK) &&
                !isShoppingListControl(wrap)
              ) {
                wrap.setAttribute(LIST_WRAP_MARK, '');
                if (isPdp) applyPdpAtcFill(wrap);
              }
            });
          }
          return;
        }
      }
      wraps.push(node);
      node = nextAncestor(node);
      depth += 1;
    }
  };

  const stretchPdpAtcAncestors = (el) => {
    markAtcListRow(el);
    const isPdp = hostKind(el) === 'pdp';
    if (isPdp) {
      let walk = nextAncestor(el);
      let walkDepth = 0;
      while (walk && walk.nodeType === 1 && !isSparkHostName(walk.localName) && walkDepth < 8) {
        const cls = typeof walk.className === 'string' ? walk.className : '';
        if (/\bsm:flex-row\b/.test(cls) && nodeHasAtc(walk, el)) {
          applyPdpAtcStack(walk);
          break;
        }
        walk = nextAncestor(walk);
        walkDepth += 1;
      }
    }
    let node = nextAncestor(el);
    let depth = 0;
    while (node && node.nodeType === 1 && !isSparkHostName(node.localName) && depth < 6) {
      if (node.hasAttribute(LIST_ROW_MARK) || node.hasAttribute(PDP_STACK_MARK)) break;
      const childCount = node.children ? node.children.length : 0;
      const onlyAtcHost =
        childCount === 0 &&
        node.shadowRoot &&
        Boolean(node.shadowRoot.querySelector(ATC_MARK_SEL)) &&
        !nodeHasList(node);
      if (
        (childCount === 1 || onlyAtcHost) &&
        !isShoppingListControl(node) &&
        !node.hasAttribute(LIST_MARK)
      ) {
        node.setAttribute(LIST_WRAP_MARK, '');
        if (isPdp) applyPdpAtcFill(node);
      } else if (isPdp && childCount >= 2 && childCount <= 4 && nodeHasAtc(node, el) && nodeHasQty(node)) {
        applyPdpAtcStack(node);
        break;
      } else {
        break;
      }
      node = nextAncestor(node);
      depth += 1;
    }
  };

  const containsOtherAtc = (node, self) => {
    if (!node) return false;
    if (nodeHasAtc(node, isAtcMarked(self) ? self : null)) {
      const atc = queryDeep(node, ATC_MARK_SEL);
      if (atc && atc !== self && !(self && self.contains && self.contains(atc))) return true;
    }
    const buttons = collectDeep(node, BUTTON_SEL, []);
    return buttons.some((b) => {
      if (b === self || (self && self.contains && self.contains(b))) return false;
      if (b.hasAttribute && b.hasAttribute(LIST_MARK)) return false;
      if (hasTwinIcon(b)) return false;
      const raw = (b.textContent || '').replace(/\s+/g, ' ').trim();
      return isAtcMarked(b) || (b.matches && b.matches('.bg-btn-raised-bg, .p-pdp-btn')) || raw === desiredLabel();
    });
  };

  const applyTwinHide = (node) => {
    if (!node || node.nodeType !== 1 || isSparkHostName(node.localName)) return false;
    if (
      isAtcMarked(node) ||
      (node.classList &&
        (node.classList.contains('fixatech-spark-card-atc') || node.classList.contains('fixatech-spark-pdp-atc')))
    ) {
      return false;
    }
    if (containsOtherAtc(node, node)) return false;
    const nestedBtns = collectDeep(node, BUTTON_SEL, []).filter(
      (b) => b !== node && !(b.hasAttribute && b.hasAttribute(LIST_MARK))
    );
    if (nestedBtns.length > 1) return false;
    if (nestedBtns.length === 1 && !hasTwinIcon(nestedBtns[0])) return false;
    node.setAttribute(LIST_MARK, '');
    if (node.classList) {
      node.classList.remove(
        'fixatech-spark-atc',
        'fixatech-spark-pdp-atc',
        'fixatech-spark-card-atc',
        'fixatech-spark-atc-hover'
      );
    }
    node.removeAttribute(LIST_WRAP_MARK);
    if (node.querySelectorAll) {
      node.querySelectorAll('.fixatech-spark-atc-label').forEach((child) => child.remove());
    }
    if (node.style) node.style.setProperty('display', 'none', 'important');
    return true;
  };

  const hideTwinWrappers = (from) => {
    let wrap = nextAncestor(from);
    let depth = 0;
    while (wrap && wrap.nodeType === 1 && !isSparkHostName(wrap.localName) && depth < 6) {
      if (wrap.hasAttribute(LIST_ROW_MARK)) break;
      if (containsOtherAtc(wrap, from)) break;
      const kids = wrap.children ? Array.from(wrap.children) : [];
      const otherKids = kids.filter((k) => k !== from && !(k.hasAttribute && k.hasAttribute(LIST_MARK)));
      if (otherKids.length > 0) break;
      if (!applyTwinHide(wrap)) break;
      wrap = nextAncestor(wrap);
      depth += 1;
    }
  };

  const hidePdpTwin = (el) => {
    if (!el || !hostKind(el)) return;
    let node = el;
    let depth = 0;
    let fallback = el.nodeType === 1 && !isSparkHostName(el.localName) ? el : null;
    while (node && node.nodeType === 1 && !isSparkHostName(node.localName) && depth < 8) {
      const isBtn = node.matches && node.matches('button, [role="button"], .bg-btn-raised-bg, .p-pdp-btn');
      const realAtc = nodeHasAtc(node) && !hasTwinIcon(node) && !isShoppingListControl(node);
      const raw = (node.textContent || '').replace(/\s+/g, ' ').trim();
      const siblingBtns = collectDeep(node, BUTTON_SEL, []).filter(
        (b) => b !== node && !(b.hasAttribute && b.hasAttribute(LIST_MARK))
      );
      const looksTwin =
        !realAtc &&
        !containsOtherAtc(node, el) &&
        siblingBtns.length <= 1 &&
        (isBtn || raw === desiredLabel() || LIST_HINT.test(raw));
      if (looksTwin) {
        applyTwinHide(node);
        hideTwinWrappers(node);
        markAtcListRow(node);
        return;
      }
      if (realAtc && isBtn) return;
      if (!containsOtherAtc(node, el)) fallback = node;
      node = nextAncestor(node);
      depth += 1;
    }
    if (fallback && !nodeHasAtc(fallback) && !containsOtherAtc(fallback, el)) {
      const fallbackBtns = collectDeep(fallback, BUTTON_SEL, []).filter(
        (b) => !(b.hasAttribute && b.hasAttribute(LIST_MARK))
      );
      if (fallbackBtns.length <= 1) {
        applyTwinHide(fallback);
        hideTwinWrappers(fallback);
      }
    }
  };

  const hideDuplicatePdpAtc = (root) => {
    if (!hostKind(root)) return;
    const label = desiredLabel();
    const buttons = collectDeep(root, BUTTON_SEL, []);
    const atcLike = buttons.filter((el) => {
      if (isQtyControl(el) || (el.hasAttribute && el.hasAttribute(LIST_MARK))) return false;
      const raw = (el.textContent || '').replace(/\s+/g, ' ').trim();
      const aria = (el.getAttribute('aria-label') || '').trim();
      return raw === label || aria === label;
    });
    atcLike.forEach((el) => {
      if (hasTwinIcon(el) || isShoppingListControl(el)) hidePdpTwin(el);
    });
    const remaining = atcLike.filter((el) => !(el.hasAttribute && el.hasAttribute(LIST_MARK)));
    if (remaining.length < 2) return;
    const keep =
      remaining.find((el) => el.classList.contains('fixatech-spark-pdp-atc') && !hasTwinIcon(el)) ||
      remaining.find((el) => el.classList.contains('fixatech-spark-card-atc') && !hasTwinIcon(el)) ||
      remaining.find((el) => el.matches && el.matches('.bg-btn-raised-bg, .p-pdp-btn') && !hasTwinIcon(el)) ||
      remaining.find((el) => !hasTwinIcon(el)) ||
      remaining[0];
    remaining.forEach((el) => {
      if (el === keep) return;
      if (keep.contains && (keep.contains(el) || el.contains(keep))) return;
      hidePdpTwin(el);
    });
  };

  const markAtc = (el) => {
    if (isShoppingListControl(el)) {
      hidePdpTwin(el);
      return;
    }
    const kind = hostKind(el);
    if (!kind) return;
    if (kind === 'card') {
      const surface = resolveAtcButton(el);
      if (isShoppingListControl(surface)) {
        hidePdpTwin(surface);
        return;
      }
      surface.classList.add('fixatech-spark-atc', 'fixatech-spark-card-atc');
      ensureBagIcon(surface);
      setLabel(surface, desiredLabel());
      bindCardHover(el, surface);
      applyPdpAtcFill(surface);
      stretchPdpAtcAncestors(surface);
      hideDuplicatePdpAtc(surface.getRootNode ? surface.getRootNode() : surface);
      return;
    }
    const surface = resolveAtcButton(el);
    if (isShoppingListControl(surface)) {
      hidePdpTwin(surface);
      return;
    }
    surface.classList.add('fixatech-spark-atc', 'fixatech-spark-pdp-atc');
    removeBagIcon(surface);
    surface.style.setProperty('background', '#ffc600', 'important');
    surface.style.setProperty('background-color', '#ffc600', 'important');
    surface.style.setProperty('color', '#141414', 'important');
    applyPdpAtcFill(surface);
    stretchPdpAtcAncestors(surface);
    hideDuplicatePdpAtc(surface.getRootNode ? surface.getRootNode() : surface);
  };

  const cleanEl = (el) => {
    if (isShoppingListControl(el)) {
      hidePdpTwin(el);
      return;
    }
    const raw = (el.textContent || '').replace(/\s+/g, ' ').trim();
    const aria = (el.getAttribute('aria-label') || '').trim();
    if (!raw) {
      if (ATC_HINT.test(aria) || SPARK_DEFAULT.test(aria)) {
        setLabel(el, desiredLabel());
        markAtc(el);
      }
      return;
    }
    if ((PRICE_SUFFIX.test(raw) && ATC_HINT.test(raw)) || SPARK_DEFAULT.test(raw)) {
      setLabel(el, desiredLabel());
      markAtc(el);
      return;
    }
    if (ATC_HINT.test(raw) || raw === desiredLabel() || ATC_HINT.test(aria)) {
      setLabel(el, desiredLabel());
      markAtc(el);
    }
  };

  const cleanAtcRoot = (root) => {
    if (!root || !root.querySelectorAll) return;
    if (hostKind(root)) {
      root.querySelectorAll(LIST_ICON_SEL).forEach((icon) => hidePdpTwin(icon));
      hideDuplicatePdpAtc(root);
    }
    root.querySelectorAll(BUTTON_SEL).forEach(cleanEl);
    const kids = root.children;
    if (!kids) return;
    for (let i = 0; i < kids.length; i += 1) {
      if (kids[i].shadowRoot) cleanAtcRoot(kids[i].shadowRoot);
    }
  };

  const hideHostTwins = (host) => {
    if (!host || !host.querySelectorAll || !hostKind(host)) return;
    host.querySelectorAll(LIST_ICON_SEL).forEach((icon) => hidePdpTwin(icon));
    host.querySelectorAll(BUTTON_SEL).forEach((el) => {
      if (isShoppingListControl(el) || hasTwinIcon(el)) hidePdpTwin(el);
    });
  };

  const THEME_CARD_SEL =
    'product-card, product-recommendations, .resource-list__item, .buy-buttons-block, .product-form-buttons, .fixatech-card';
  const THEME_ATC_SEL =
    'add-to-cart-component, .add-to-cart-button, .quick-add__button, [data-fixatech-atc], .fixatech-card__atc-btn';

  const isThemeAtc = (el) => Boolean(el && el.matches && el.matches(THEME_ATC_SEL));

  const stretchAtcLike = (el) => {
    if (!el || !el.style) return;
    if (el.localName === 'spark-product-card') {
      const delay = isDelayCard(el);
      el.style.setProperty('display', 'flex', 'important');
      el.style.setProperty('flex-direction', 'column', 'important');
      el.style.setProperty('flex', delay ? '0 0 auto' : '1 1 0%', 'important');
      el.style.setProperty('width', '100%', 'important');
      el.style.setProperty('max-width', '100%', 'important');
      el.style.setProperty('min-width', '0', 'important');
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('min-height', '0', 'important');
      el.style.setProperty('overflow', 'hidden', 'important');
      el.style.setProperty('box-sizing', 'border-box', 'important');
      el.style.setProperty('align-self', 'stretch', 'important');
      if (delay) pinDelayLightFooter(el);
      return;
    }
    el.style.setProperty('width', '100%', 'important');
    el.style.setProperty('max-width', 'none', 'important');
    el.style.setProperty('min-width', '0', 'important');
    el.style.setProperty('flex', '1 1 100%', 'important');
    el.style.setProperty('box-sizing', 'border-box', 'important');
    el.style.setProperty('align-self', 'stretch', 'important');
  };

  const hideLightTwin = (el) => {
    if (!el || isSparkHostName(el.localName) || isThemeAtc(el) || isAtcMarked(el) || isQtyControl(el)) {
      return false;
    }
    return applyTwinHide(el);
  };

  /* Light-DOM heart beside spark-product-card / theme ATC (recommended carousel).
     Never run on spark-pdp — nowrap LIST_ROW here pins yellow ATC beside qty. */
  const hideAdjacentTwins = (host) => {
    if (!host || !host.parentElement || host.localName === 'spark-pdp') return;
    let parent = host.parentElement;
    let depth = 0;
    while (parent && parent.nodeType === 1 && depth < 5) {
      const kids = parent.children ? Array.from(parent.children) : [];
      kids.forEach((sib) => {
        if (sib === host) return;
        if (isShoppingListControl(sib) || hasTwinIcon(sib)) hideLightTwin(sib);
      });
      const hidden = kids.filter((k) => k.hasAttribute && k.hasAttribute(LIST_MARK));
      if (hidden.length && kids.length >= 2 && kids.length <= 6) {
        parent.setAttribute(LIST_ROW_MARK, '');
        stretchAtcLike(host);
        host.setAttribute(LIST_WRAP_MARK, '');
        kids.forEach((k) => {
          if (k === host || (k.hasAttribute && k.hasAttribute(LIST_MARK))) return;
          if (isThemeAtc(k) || isAtcMarked(k) || k.localName === 'spark-product-card') stretchAtcLike(k);
        });
      }
      if (parent.matches && parent.matches('product-card, product-recommendations, body, html')) break;
      parent = parent.parentElement;
      depth += 1;
    }
  };

  const hideThemeCardTwins = (root) => {
    if (!hasSparkHosts()) return;
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(THEME_CARD_SEL).forEach((card) => {
      card.querySelectorAll(BUTTON_SEL).forEach((el) => {
        if (isThemeAtc(el) || isAtcMarked(el) || isQtyControl(el)) return;
        if (isShoppingListControl(el) || hasTwinIcon(el)) hideLightTwin(el);
      });
      card.querySelectorAll(HOST_SEL).forEach((host) => {
        if (host.localName === 'spark-pdp') return;
        hideAdjacentTwins(host);
        stretchAtcLike(host);
      });
    });
  };

  const paintAtcRoot = (root) => {
    if (!root) return;
    const vars = varsForKind(hostKind(root));
    if (root.host && vars) writeAtcVars(root.host, vars);
    injectAtcShadow(root);
    injectPriceShadow(root);
    labelSparkPrices(root);
    cleanAtcRoot(root);
    pinSparkCardFooter(root);
    if (root.host) {
      bindCardNavGuard(root.host);
      hideHostTwins(root.host);
      if (root.host.localName !== 'spark-pdp') hideAdjacentTwins(root.host);
    }
  };

  const watchAtcHost = (host) => {
    if (host.shadowRoot) {
      paintAtcRoot(host.shadowRoot);
      return;
    }
    waitForShadow(host, () => paintAtcRoot(host.shadowRoot));
  };

  const priceLabels = () => {
    const lang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    return lang.indexOf('fr') === 0
      ? { sale: 'Votre prix:', rrp: 'Prix régulier:' }
      : { sale: 'Your price:', rrp: 'Regular price:' };
  };

  const priceInIgnored = (el) => {
    let node = el;
    while (node && node.nodeType === 1) {
      const name = node.localName;
      if (
        name === 'table' ||
        name === 'tr' ||
        name === 'td' ||
        name === 'th' ||
        name === 'thead' ||
        name === 'tbody'
      ) {
        return true;
      }
      if (node.matches && node.matches('button, [role="button"], .bg-btn-raised-bg, .p-pdp-btn')) return true;
      if (node.hasAttribute && (node.hasAttribute(LIST_MARK) || node.hasAttribute(LIST_ROW_MARK))) return true;
      node = node.parentElement;
    }
    return false;
  };

  const stripKnownPrefixes = (value, known) => {
    let rest = value;
    for (let i = 0; i < 4; i += 1) {
      const stripped = rest.replace(/^\s+/, '').replace(known, '');
      if (stripped === rest.replace(/^\s+/, '')) break;
      rest = stripped;
    }
    return rest;
  };

  const applyPricePrefix = (el, label, known) => {
    if (!el || el.nodeType !== 1) return;
    const next = `${label} `;
    const existing = el.querySelector(`[${PRICE_LABEL_MARK}]`);
    const prev = el.previousElementSibling;
    const prevIsLabel = prev && known.test((prev.textContent || '').replace(/\s+/g, ' ').trim());
    if (prevIsLabel && prev.textContent !== next) prev.textContent = next;

    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (!node.nodeValue || !node.nodeValue.trim()) continue;
      if (existing && existing.contains(node)) continue;
      const rest = stripKnownPrefixes(node.nodeValue, known);
      if (rest !== node.nodeValue) node.nodeValue = rest;
    }

    if (prevIsLabel) {
      if (existing) existing.remove();
      return;
    }
    if (existing) {
      if (existing.textContent !== next) existing.textContent = next;
      return;
    }
    const span = document.createElement('span');
    span.setAttribute(PRICE_LABEL_MARK, '');
    span.textContent = next;
    el.insertBefore(span, el.firstChild);
  };

  const injectPriceShadow = (root) => {
    if (!root || !root.querySelector) return;
    const kind = hostKind(root);
    if (kind !== 'pdp' && kind !== 'card') return;
    let style = root.querySelector(`style[${PRICE_SHADOW_MARK}]`);
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(PRICE_SHADOW_MARK, '');
    }
    const css =
      kind === 'card'
        ? ':host{--spark-pricing-font-weight:700;--spark-pricing-rrp-color:#333333;}' + PRICE_CARD_CSS
        : ':host{--spark-pricing-fontsize:32px;--spark-pricing-font-weight:400;--spark-pricing-rrp-color:#333333;}' +
          PRICE_CSS;
    if (style.textContent !== css) style.textContent = css;
    if (style.parentNode !== root || root.lastChild !== style) root.appendChild(style);
  };

  const labelSparkPrices = (root) => {
    if (!root || !root.querySelectorAll) return;
    const kind = hostKind(root);
    if (kind !== 'pdp' && kind !== 'card') return;
    const labels = priceLabels();
    const sales = [];
    root.querySelectorAll(PRICE_SALE_SEL).forEach((el) => {
      if (el.matches && el.matches(PRICE_RRP_SEL)) return;
      if (priceInIgnored(el)) return;
      sales.push(el);
    });
    const rrps = [];
    root.querySelectorAll(PRICE_RRP_SEL).forEach((el) => {
      if (priceInIgnored(el)) return;
      rrps.push(el);
    });
    sales.forEach((el) => {
      el.setAttribute(PRICE_SALE_MARK, '');
      applyPricePrefix(el, labels.sale, PRICE_SALE_PREFIX);
    });
    rrps.forEach((el) => {
      el.setAttribute(PRICE_RRP_MARK, '');
      applyPricePrefix(el, labels.rrp, PRICE_RRP_PREFIX);
    });
    sales.forEach((sale) => {
      const parent = sale.parentElement;
      if (!parent) return;
      const siblingRrp = rrps.find((rrp) => rrp.parentElement === parent);
      if (siblingRrp && parent.children.length <= 4) {
        parent.setAttribute(PRICE_STACK_MARK, '');
      }
    });
  };

  const paintRoot = (root) => {
    if (!root) return;
    if (root.host) writeAtcVars(root.host, varsForKind(hostKind(root)));
    injectAtcShadow(root);
    injectPriceShadow(root);
    labelSparkPrices(root);
    if (!root.querySelector(`style[${MARK}]`)) {
      const style = document.createElement('style');
      style.setAttribute(MARK, '');
      style.textContent = CSS;
      root.appendChild(style);
    }
    hideRows(root);
    paintAtcRoot(root);
  };

  const pendingHosts = new Set();
  let paintAllPending = false;
  let paintTimer = 0;
  const DEBOUNCE_MS = 120;

  const flushPaint = () => {
    paintTimer = 0;
    applyAtcTokens();
    const paintHost = (host) => {
      if (host.shadowRoot) paintRoot(host.shadowRoot);
      else waitForShadow(host);
      if (host.localName !== 'spark-pdp') hideAdjacentTwins(host);
    };
    if (paintAllPending) {
      paintAllPending = false;
      pendingHosts.clear();
      collectHosts().forEach(paintHost);
      hideThemeCardTwins(document);
      watchLateMounts();
      collectHosts().forEach(bindCardNavGuard);
      return;
    }
    pendingHosts.forEach(paintHost);
    pendingHosts.clear();
    hideThemeCardTwins(document);
    watchLateMounts();
    collectHosts().forEach(bindCardNavGuard);
  };

  const scheduleFlush = () => {
    if (paintTimer) return;
    paintTimer = setTimeout(flushPaint, DEBOUNCE_MS);
  };

  const scheduleHost = (host) => {
    pendingHosts.add(host);
    scheduleFlush();
  };

  const scheduleAll = () => {
    paintAllPending = true;
    scheduleFlush();
  };

  const waitingShadow = new WeakSet();
  const waitForShadow = (host, cb) => {
    if (host.shadowRoot) {
      if (cb) cb();
      else scheduleHost(host);
      return;
    }
    if (waitingShadow.has(host)) return;
    waitingShadow.add(host);
    const tryOnce = () => {
      if (!host.shadowRoot) return false;
      waitingShadow.delete(host);
      if (cb) cb();
      else scheduleHost(host);
      return true;
    };
    const afterDefined = () => {
      if (tryOnce()) return;
      requestAnimationFrame(() => {
        if (tryOnce()) return;
        setTimeout(() => {
          if (!tryOnce()) waitingShadow.delete(host);
        }, 200);
      });
    };
    const name = host.localName;
    if (window.customElements && customElements.get(name)) afterDefined();
    else if (window.customElements && customElements.whenDefined) customElements.whenDefined(name).then(afterDefined).catch(() => {});
    else afterDefined();
  };

  const observedRoots = new WeakSet();
  const watchHostShadow = (host, root) => {
    if (observedRoots.has(root)) return;
    observedRoots.add(root);
    new MutationObserver(() => scheduleHost(host)).observe(root, { childList: true, subtree: true });
  };

  const watchPdpShadows = (host, root) => {
    if (!root) return;
    watchHostShadow(host, root);
    if (!root.querySelectorAll) return;
    root.querySelectorAll('*').forEach((el) => {
      if (el.shadowRoot) watchPdpShadows(host, el.shadowRoot);
    });
  };

  const paint = (host) => {
    if (!host.shadowRoot) return false;
    paintRoot(host.shadowRoot);
    if (host.localName === 'spark-pdp') watchPdpShadows(host, host.shadowRoot);
    else watchHostShadow(host, host.shadowRoot);
    return true;
  };

  const watch = (host) => {
    if (host.shadowRoot) {
      paint(host);
      return;
    }
    waitForShadow(host, () => paint(host));
  };

  const isLateHit = (node) => {
    if (!node || node.nodeType !== 1) return false;
    const name = node.localName || '';
    if (isSparkHostName(name) || name === 'product-card' || name === 'article' || name.indexOf('spark-') === 0) {
      return true;
    }
    return Boolean(
      node.querySelector && node.querySelector(`${HOST_SEL}, product-card, .fixatech-card, .product-grid__item`)
    );
  };

  const onLateMount = () => {
    collectHosts().forEach(bindCardNavGuard);
    if (hasSparkHosts()) scheduleAll();
    else arm();
  };

  const watchingLate = new WeakSet();
  const watchLateMounts = () => {
    document.querySelectorAll(LATE_SEL).forEach((el) => {
      if (watchingLate.has(el)) return;
      watchingLate.add(el);
      new MutationObserver((records) => {
        for (let i = 0; i < records.length; i += 1) {
          const added = records[i].addedNodes;
          for (let j = 0; j < added.length; j += 1) {
            if (!isLateHit(added[j])) continue;
            onLateMount();
            return;
          }
        }
      }).observe(el, { childList: true, subtree: true });
    });
  };

  const start = () => {
    if (!hasSparkHosts()) return;
    scheduleAll();
    collectHosts().forEach((host) => {
      bindCardNavGuard(host);
      if (!host.shadowRoot) return;
      if (host.localName === 'spark-pdp') watchPdpShadows(host, host.shadowRoot);
      else watchHostShadow(host, host.shadowRoot);
    });
    watchLateMounts();
  };

  const arm = () => {
    watchLateMounts();
    if (hasSparkHosts()) start();
  };

  document.addEventListener('click', onDocumentSparkCardNavCapture, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
  else arm();

  document.addEventListener('sparklayer:ready', () => {
    watchLateMounts();
    collectHosts().forEach(bindCardNavGuard);
    if (hasSparkHosts()) scheduleAll();
  });

  document.addEventListener('shopify:section:load', () => {
    watchLateMounts();
    collectHosts().forEach(bindCardNavGuard);
    if (hasSparkHosts()) scheduleAll();
  });

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('spark-pdp').then(arm).catch(() => {});
    customElements.whenDefined('spark-product-card').then(arm).catch(() => {});
  }
})();
